import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import ActionEditor from './ActionEditor';
import SearchPalette from './SearchPalette';
import DailyBrief from './DailyBrief';
import CalendarView from './CalendarView';
import Ledger from './Ledger';
import Planting from './Planting';
import AI_PROMPT from '../aiPrompt';
import MilestoneTimeline from './MilestoneTimeline';
import { ChevronUp, ChevronDown, Plus, X, Target, CheckCircle2, Circle, AlertTriangle, Calendar, Check, Zap, Archive, RotateCcw, ChevronRight, FolderPlus, Building, Briefcase, Palette, Sun, Moon, Leaf, Coffee, Gamepad2, Focus, Camera, Aperture, Eye, Maximize2, Minimize2, Upload, Download, Columns2, Flag, Copy, Search, BookOpen } from 'lucide-react';

// Theme Context
import { themes, ThemeProvider, useTheme, ThemeSwitcher, PhoIcon } from '../theme';
import DetailPane from './DetailPane';
import KanbanBoard from './KanbanBoard';
import Sidebar from './Sidebar';
import GoalsRoom from './GoalsRoom';
import { API_BASE } from '../config';
import { fmtCivil, isPast } from '../dates';
import { WP_LIST_CSS } from '../workingPapers';


const DailyFocusTool = ({ token, user, onLogout }) => {
  const { currentTheme, switchTheme, theme } = useTheme();
  const toggleActionPho = (actionId) => {
    const d = dataRef.current;
    const action = [...d.actions, ...d.completedActions].find(g => g.id === actionId);
    if (!action) return;

    // Visual feedback
    setPhoToggleFlash(prev => new Set(prev).add(actionId));
    setTimeout(() => {
      setPhoToggleFlash(prev => {
        const newSet = new Set(prev);
        newSet.delete(actionId);
        return newSet;
      });
    }, 600);

    updateAction(actionId, { pho: !action.pho });
  };
  const [goals, setGoals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [actions, setActions] = useState([]);
  const [completedActions, setCompletedActions] = useState([]);
  const [deferredActions, setDeferredActions] = useState([]);
  const [archivedActions, setArchivedActions] = useState([]);
  const [trash, setTrash] = useState([]);           // 30-day tombstones (see CONTAINERS-DESIGN.md)
  const [undoToast, setUndoToast] = useState(null); // { message, restore, plain? }
  // U asks before it acts: one keystroke used to silently strip every star in
  // the workspace — no confirm, no undo, no toast (UX-6).
  const [confirmUnstarAll, setConfirmUnstarAll] = useState(false);
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newAction, setNewAction] = useState('');
  // ✦ Focus survives a reload — starring three things for the day and losing
  // the filter on every refresh made the promise a lie. Paired with the
  // start-view preference below, "Board in Focus" is a real front door.
  const [focusMode, setFocusMode] = useState(() => {
    try { return localStorage.getItem('phokii-focus') === '1'; } catch { return false; }
  });
  useEffect(() => { try { localStorage.setItem('phokii-focus', focusMode ? '1' : '0'); } catch {} }, [focusMode]);
  const [zenMode, setZenMode] = useState(false);        // H — selected action full-screen
  // Leaving zen used to be a hard cut. This marks the return so the surfaces
  // coming back can fade in around the note instead of snapping into place.
  const [zenReturning, setZenReturning] = useState(false);
  const zenWasRef = useRef(false);
  useEffect(() => {
    if (zenWasRef.current && !zenMode) {
      setZenReturning(true);
      const t = setTimeout(() => setZenReturning(false), 320);
      zenWasRef.current = zenMode;
      return () => clearTimeout(t);
    }
    zenWasRef.current = zenMode;
  }, [zenMode]);
  // Where a session opens is the USER'S choice (the deferred half of UX-5,
  // built Sep 2026 after the founder — the only user — churned on booting
  // into the shelf every morning). 'goals' stays the unset default so the
  // shelf remains the stranger's first impression; the picker lives under
  // ··· More → "Start on". Daily use pays no toll at the bookstore.
  const startView = (() => {
    try { return localStorage.getItem('phokii-start-view') || 'goals'; } catch { return 'goals'; }
  })();
  const [calendarView, setCalendarView] = useState(startView === 'calendar'); // C — do/due calendar
  // The WALL is home now (Paul, Oct 9 2026) — the question-home is parked,
  // intact, behind G. The 'goals' start pref boots onto the wall.
  const [ledgerView, setLedgerView] = useState(false);
  const [ledgerJump, setLedgerJump] = useState(null);      // deep link: { type, id, n }
  const openLedgerAt = (type, id) => {
    setLedgerJump({ type, id, n: Date.now() });
    setLedgerView(true);
    setKanbanMode(false); setMilestoneView(false); setCalendarView(false);
  };
  // A card on the home grid opens the project SOLO — straight into the work,
  // in whichever of List/Board was used last (sticky), wearing the comp's
  // minimal room: no sidebar, no nav, one masthead. The loop is
  // home ⇄ project: two levels, one key (Paul, Oct 7 2026).
  const [soloFromHome, setSoloFromHome] = useState(false);
  // The planning room (mock G, Oct 14 2026): plan/see/load goals. An overlay
  // over whatever you were on — never a boot destination, never counted.
  const [goalsRoomOpen, setGoalsRoomOpen] = useState(false);
  const [superView, setSuperView] = useState(startView === 'goals');
  const workViewRef = useRef(null);
  if (workViewRef.current == null) {
    try { workViewRef.current = localStorage.getItem('phokii-work-view') === 'board' ? 'board' : 'list'; }
    catch { workViewRef.current = 'list'; }
  }
  const openProjectSolo = (projectId) => {
    setSoloFromHome(true); setSuperView(false);
    setOpenFeatureId(projectId); setOpenGoalId(null);
    setSelectedActionId(null);
    setZenMode(false); setMilestoneView(false); setCalendarView(false);
    setKanbanMode(workViewRef.current === 'board');
    setLedgerView(false);
    try { localStorage.setItem('phokii-last-solo', String(projectId)); } catch { /* convenience only */ }
  };
  const closeSoloToHome = () => {
    setSoloFromHome(false);
    setOpenFeatureId(null); setOpenGoalId(null);
    openSuperView(); // home is the wall
  };
  // ⇧B — THE DECK (mock B, judged Oct 7): every project in a rail with its
  // counts, one project's board at full size, jump with one click.
  const lastSoloOrFirst = () => {
    const alive = projects.filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name));
    let pid = null;
    try { pid = parseInt(localStorage.getItem('phokii-last-solo'), 10); } catch { /* fall through */ }
    if (!alive.some(p => p.id === pid)) pid = alive[0] ? alive[0].id : null;
    return pid;
  };
  const openSuperView = () => {
    setSuperView(true); setSoloFromHome(false);
    setSelectedActionId(null); setWallSolo(new Set());
    setZenMode(false); setMilestoneView(false); setCalendarView(false); setLedgerView(false);
  };
  // The wall's strips are YOURS to order, left = priority — a DAW session
  // reorders its tracks (Paul, Oct 13 2026). Manual order lives in the
  // data (wallOrder), alphabetical is the fallback for the unranked, so
  // new projects appear at the right until placed.
  const wallCompare = (a, b) =>
    ((a.wallOrder ?? 1e9) - (b.wallOrder ?? 1e9)) || a.name.localeCompare(b.name);
  const [wallDragOver, setWallDragOver] = useState(null); // { id, before }
  const reorderWallStrips = (draggedId, targetId, before) => {
    if (draggedId === targetId) return;
    const d = dataRef.current;
    const ids = [...d.projects].filter(pr => pr.active).sort(wallCompare).map(pr => pr.id)
      .filter(id => id !== draggedId);
    let idx = ids.indexOf(targetId);
    if (idx < 0) return;
    if (!before) idx += 1;
    ids.splice(idx, 0, draggedId);
    const orderOf = new Map(ids.map((id, i) => [id, i * 10]));
    persist({ projects: d.projects.map(pr => orderOf.has(pr.id) ? { ...pr, wallOrder: orderOf.get(pr.id) } : pr) });
  };

  // Plain L/B pressed in Goals: straight to the work — the last room you
  // were in, in that arrangement.
  const openWorkFromGoals = (arrangement) => {
    const pid = lastSoloOrFirst();
    if (pid == null) return;
    openProjectSolo(pid);
    setKanbanMode(arrangement === 'board');
  };
  // Goals mode unmounts when you switch views; this ref carries its position
  // across so returning resumes the goal/project/action you were on.
  const ledgerPosRef = useRef({ sel: null, openActionId: null, pageTwo: false });
  // W, first-run — the goals-first front door. Truthy means open; the value
  // carries which stage it opens at ('library' comes from the shelf's quiet
  // line, anything else starts at the goal question).
  const [plantingOpen, setPlantingOpen] = useState(false);
  const [zenHint, setZenHint] = useState(false);        // brief "select an action first" toast
  const [filmstrip, setFilmstrip] = useState(false);    // zen: top-edge strip of all notes in the lens
  const [searchOpen, setSearchOpen] = useState(false);  // ⌘K palette
  const [briefOpen, setBriefOpen] = useState(false);    // Daily Brief (B, auto once per morning)
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth);
  const [expandedGoals, setExpandedGoals] = useState(new Set());
  const [editingProject, setEditingProject] = useState(null);
  const [editingProjectName, setEditingProjectName] = useState(null);
  const [editingGoalName, setEditingGoalName] = useState(null);
  const [editingAction, setEditingAction] = useState(null);
  const [editingActionLink, setEditingActionLink] = useState(null);
  const [showAddActionModal, setShowAddActionModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInlineAdd, setShowInlineAdd] = useState(false);
  const [openFeatureId, setOpenFeatureId] = useState(null);
  const [openGoalId, setOpenGoalId] = useState(null); // sidebar goal solo: one goal's projects + actions
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [dismissedColdOpenFor, setDismissedColdOpenFor] = useState(new Set());
  const [kanbanMode, setKanbanMode] = useState(startView === 'board');
  const [dragOverCol, setDragOverCol] = useState(null);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [createModalTab, setCreateModalTab] = useState('action');
  const [bulkImportText, setBulkImportText] = useState('');
  const [importTargetProject, setImportTargetProject] = useState('');
  const [showBatchAssignModal, setShowBatchAssignModal] = useState(false);
  const [showDeferredReview, setShowDeferredReview] = useState(false);
  const [selectedActions, setSelectedActions] = useState(new Set());
  const [showCompleted, setShowCompleted] = useState(false);
  const [todaysFocusCollapsed, setTodaysFocusCollapsed] = useState(
    () => localStorage.getItem('todaysFocusCollapsed') === 'true'
  );
  const [completingAction, setCompletingAction] = useState(null);
  const [completionModal, setCompletionModal] = useState(null); // { actionId, actionText }
  const [completionResolution, setCompletionResolution] = useState('done');
  const [completionComment, setCompletionComment] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [deferringActionId, setDeferringActionId] = useState(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistTargetId, setChecklistTargetId] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showProjectImportModal, setShowProjectImportModal] = useState(false);
  const [projectImportJson, setProjectImportJson] = useState('');
  const [projectImportError, setProjectImportError] = useState('');
  const [projectImportResult, setProjectImportResult] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [nextMilestoneId, setNextMilestoneId] = useState(1);
  const [nextActionId, setNextActionId] = useState(1);
  const [nextProjectId, setNextProjectId] = useState(1);
  const [nextGoalId, setNextGoalId] = useState(1);
  const [milestoneView, setMilestoneView] = useState(false);
  const [addingProjectForGoal, setAddingProjectForGoal] = useState(null); // null | goalId | 'orphan'
  const [inlineProjectName, setInlineProjectName] = useState('');
  const [addingGoal, setAddingGoal] = useState(false);
  const [inlineGoalName, setInlineGoalName] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');
  const [managementMode, setManagementMode] = useState(false);
  // Sticky work view: whichever of List/Board the hands last touched is what
  // the next card click opens. Goals, Calendar and zen never overwrite it.
  useEffect(() => {
    if (zenMode || ledgerView || calendarView || milestoneView) return;
    const v = kanbanMode ? 'board' : 'list';
    workViewRef.current = v;
    try { localStorage.setItem('phokii-work-view', v); } catch { /* per-browser convenience only */ }
  }, [kanbanMode, ledgerView, calendarView, milestoneView, zenMode]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('phokii-sidebar-collapsed') === 'true');
  // The Goals home draws its own top line (wordmark, lens, log out) — the
  // unified header stands down there and returns the moment a book opens.
  const [ledgerAtHome, setLedgerAtHome] = useState(true);
  // A real project is open solo (any origin) — the one case where repeating
  // the project's name on banners and cards is pure noise.
  const soloProject = openFeatureId != null && openFeatureId !== 'unassigned';
  // The comp's minimal room: a solo opened FROM THE HOME GRID drops the
  // sidebar, the nav and the crumb strip, and wears one masthead.
  const minimalSolo = soloFromHome && soloProject && !ledgerView && !calendarView && !milestoneView && !zenMode;
  // ⇧B — THE WALL (mock A at post-cleanup scale, Paul's "Go!" Oct 9 2026):
  // every project as a fixed-width mixer strip, in motion on top. It
  // replaced the deck. The DAW law: a strip never changes width because
  // the session gained strips — side-scroll instead.
  const wallMode = superView && !ledgerView && !calendarView && !milestoneView && !zenMode;
  // S solos a strip in place (dims the rest); ⇧-click S stacks solos.
  const [wallSolo, setWallSolo] = useState(() => new Set());
  // The task editor docks like a DAW: bottom clip-view or right inspector.
  const [paneDock, setPaneDock] = useState(() => {
    try { return localStorage.getItem('phokii-pane-dock') === 'right' ? 'right' : 'bottom'; } catch { return 'bottom'; }
  });
  useEffect(() => {
    try { localStorage.setItem('phokii-pane-dock', paneDock); } catch { /* convenience only */ }
  }, [paneDock]);
  // The wall's lens: Everything, or one goal. Sticky.
  const [wallLens, setWallLens] = useState(() => {
    try { const v = localStorage.getItem('phokii-wall-lens'); return v ? parseInt(v, 10) : null; } catch { return null; }
  });
  useEffect(() => {
    try { wallLens == null ? localStorage.removeItem('phokii-wall-lens') : localStorage.setItem('phokii-wall-lens', String(wallLens)); } catch { /* convenience only */ }
  }, [wallLens]);
  // The room list is Bear-like: tasks in a fixed rail on the left, the note
  // always open on the right. Click in and write. (Paul, Oct 7 2026.)
  const roomList = minimalSolo && !kanbanMode;
  // The chips: the crumb reborn as doors (Paul, Oct 8 2026). The goal chip
  // re-aims the goal, the project chip jumps projects — switchers, never
  // wideners. "All" keeps its two homes: the home grid and the deck (⇧B).
  const [roomPicker, setRoomPicker] = useState(null); // {stage:'goals'} | {stage:'projects', goalId}
  const roomPickerRef = useRef(null);
  useEffect(() => {
    if (!roomPicker) return;
    const onDown = (e) => { if (roomPickerRef.current && !roomPickerRef.current.contains(e.target)) setRoomPicker(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setRoomPicker(null); } };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey, true); };
  }, [roomPicker]);
  // The note defaults open: the remembered one for this project, else the
  // first task. Only when nothing is open — a choice is never overridden.
  useEffect(() => {
    if (!roomList || selectedActionId != null) return;
    const mine = actions.filter(a => a.linkType === 'project' && a.linkId === openFeatureId && !a.completed);
    if (mine.length === 0) return;
    let remembered = null;
    try { remembered = JSON.parse(localStorage.getItem('phokii-room-note') || '{}')[openFeatureId]; } catch { /* convenience only */ }
    const pick = mine.find(a => a.id === remembered) || mine[0];
    setSelectedActionId(pick.id);
  }, [roomList, openFeatureId, actions, selectedActionId]);
  // …and sticky: the room reopens on the note you left it on.
  useEffect(() => {
    if (!minimalSolo || selectedActionId == null || openFeatureId == null || openFeatureId === 'unassigned') return;
    try {
      const m = JSON.parse(localStorage.getItem('phokii-room-note') || '{}');
      m[openFeatureId] = selectedActionId;
      localStorage.setItem('phokii-room-note', JSON.stringify(m));
    } catch { /* convenience only */ }
  }, [selectedActionId, openFeatureId, minimalSolo]);
  useEffect(() => { localStorage.setItem('phokii-sidebar-collapsed', String(sidebarCollapsed)); }, [sidebarCollapsed]);
  // Zen only makes sense with an open action — bail out if it closes underneath us.
  // (Hooks must stay above the `if (loading)` early return further down.)
  useEffect(() => { if (zenMode && !selectedActionId) setZenMode(false); }, [zenMode, selectedActionId]);

  // Zen navigation walks the list the user was just looking at (same project
  // filter, same focus filter, same sort). Assigned each render, read by the
  // key handler and the ‹ › chips.
  const zenListRef = useRef([]);
  const zenNavDirRef = useRef(0);   // last travel direction: +1 arrived from above, -1 from below
  const zenFocusNextRef = useRef(false); // focus the note after nav ONLY when the nav came from typing (boundary flow)
  const zenClickEnterRef = useRef(false); // zen entered by clicking into the note — don't yank the scroll position
  const navigateZen = useCallback((dir, focusAfter = false) => {
    const list = zenListRef.current;
    if (!list.length) return;
    const idx = list.findIndex(g => g.id === selectedActionId);
    // Current action outside the lens (filtered out / completed): start at an end
    const next = idx === -1
      ? (dir > 0 ? list[0] : list[list.length - 1])
      : list[(idx + dir + list.length) % list.length];
    zenNavDirRef.current = dir;
    zenFocusNextRef.current = focusAfter;
    setSelectedActionId(next.id);
  }, [selectedActionId]);

  // The cursor only enters a note deliberately: when the user clicks into it,
  // or when they flowed here from the previous note's edge while typing.
  // Entering zen by itself never steals focus — so H toggles cleanly out.
  useEffect(() => {
    if (!zenMode) {
      zenNavDirRef.current = 0;
      setFilmstrip(false);
      window.scrollTo(0, 0); // panels come back at the top of the page
      return;
    }
    if (!zenFocusNextRef.current) {
      // Entered by clicking into the note: keep the caret and scroll position.
      // Entered via H / arrived via chips, swipe, arrows: start at the top.
      if (zenClickEnterRef.current) zenClickEnterRef.current = false;
      else window.scrollTo(0, 0);
      return;
    }
    zenFocusNextRef.current = false;
    const where = zenNavDirRef.current > 0 ? 'start' : 'end';
    const t = setTimeout(() => {
      actionEditorRef.current?.focus?.(where);
      const el = document.querySelector('.action-editor .tiptap');
      if (el && !el.contains(document.activeElement)) el.focus();
    }, 120);
    return () => clearTimeout(t);
  }, [zenMode, selectedActionId]);

  // Closing the pane always lands the user back at the top of the page
  useEffect(() => { if (!selectedActionId) window.scrollTo(0, 0); }, [selectedActionId]);

  // Dev-only demo driver: ?demo=pane|zen|board poses the app for headless
  // marketing screenshots. Compiled out of nothing in production behavior —
  // the env check makes it a no-op there.
  useEffect(() => {
    if (window.location.hostname !== 'localhost' || loading) return;
    const mode = new URLSearchParams(window.location.search).get('demo');
    if (!mode) return;
    const t = setTimeout(() => {
      const withNotes = dataRef.current.actions.find(g => (g.notes || '').length > 200) || dataRef.current.actions[0];
      if (mode === 'board') setKanbanMode(true);
      if (mode === 'calendar') setCalendarView(true);
      if (mode === 'brief') setBriefOpen(true);
      if ((mode === 'pane' || mode === 'zen') && withNotes) setSelectedActionId(withNotes.id);
      if (mode === 'zen' && withNotes) setTimeout(() => setZenMode(true), 400);
    }, 600);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // The morning rounds no longer greet anyone (Paul, Oct 7 2026: "no morning
  // rounds, no shelf, nothing like that at all"). The component remains in
  // the codebase for later weed-pulling; nothing opens it.

  // Goals-first: a brand-new space opens on the Planting screen, not a list
  useEffect(() => {
    if (loading) return;
    const d = dataRef.current;
    if (!d.goals.length && !d.projects.length && !d.actions.length) setPlantingOpen(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Filmstrip: any wheel motion scrolls the strip sideways (its natural axis)
  useEffect(() => {
    if (!filmstrip) return;
    const el = document.querySelector('.zen-filmstrip .strip-scroll');
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      e.stopPropagation(); // never let the pane's swipe handler see strip scrolls
      el.scrollLeft += (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [filmstrip]);

  // Swipe navigation in zen — trackpad (wheel deltaX) and touchscreen.
  // overscroll-behavior/touch-action on the pane keep the browser's own
  // back/forward gestures out of the way.
  const zenPaneRef = useRef(null);
  // In zen the note is the only thing on screen, so the empty margins around
  // it mean "I'm done" — clicking there closes it, like Esc. Text selection
  // that merely ends outside the note must not count, and neither may the
  // chips or the filmstrip, which are fixed and live outside the note's box.
  useEffect(() => {
    if (!zenMode) return;
    const onDown = (e) => {
      const t = e.target;
      if (zenPaneRef.current && zenPaneRef.current.contains(t)) return;
      if (t.closest && t.closest('[data-zen-chrome]')) return;
      if (t.closest && t.closest('input, textarea, button, [contenteditable="true"]')) return;
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return; // finishing a drag-select
      setZenMode(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [zenMode]);
  useEffect(() => {
    if (!zenMode) return;
    const el = zenPaneRef.current;
    if (!el) return;
    let acc = 0, locked = false, settle = null;
    const springBack = () => {
      el.style.transition = 'transform 0.18s ease';
      el.style.transform = '';
      setTimeout(() => { if (el) el.style.transition = ''; }, 200);
    };
    const onWheel = (e) => {
      if (e.target.closest?.('.zen-filmstrip')) return; // the strip scrolls itself
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY) * 1.5) return; // vertical scroll stays native
      e.preventDefault();
      clearTimeout(settle);
      // Trackpad momentum keeps emitting decaying deltas long after fingers
      // lift, with pauses inside the tail — hold the lock until a long
      // silence so one physical swipe commits exactly once.
      settle = setTimeout(() => { acc = 0; locked = false; springBack(); }, 500);
      if (locked) return;
      acc += e.deltaX;
      el.style.transform = `translateX(${Math.max(-44, Math.min(44, -acc * 0.25))}px)`;
      if (Math.abs(acc) > 130) {
        const dir = acc > 0 ? 1 : -1; // fingers left → content left → next
        locked = true;
        acc = 0;
        springBack();
        navigateZen(dir);
      }
    };
    let tx = null, ty = null, tDone = false;
    const onTouchStart = (e) => {
      if (e.target.closest?.('.zen-filmstrip')) { tx = null; return; }
      tx = e.touches[0].clientX; ty = e.touches[0].clientY; tDone = false;
    };
    const onTouchMove = (e) => {
      if (tDone || tx === null) return;
      const dx = e.touches[0].clientX - tx;
      const dy = e.touches[0].clientY - ty;
      if (Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy) * 2) {
        tDone = true;
        navigateZen(dx < 0 ? 1 : -1);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    return () => {
      clearTimeout(settle);
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
    };
  }, [zenMode, navigateZen]);
  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [showThemePicker, setShowThemePicker] = useState(false);
  // Goal color view mode
  const [groupByProject, setGroupByProject] = useState(true);
  const [listWidth, setListWidth] = useState(() => {
    const saved = localStorage.getItem('phokii-list-width');
    return saved ? parseInt(saved, 10) : 380;
  });
  const [kanbanWidth, setKanbanWidth] = useState(() => {
    const saved = localStorage.getItem('phokii-kanban-width');
    return saved ? parseInt(saved, 10) : 640;
  });
  const resizeDrag = useRef(null);
  const actionEditorRef = useRef(null);

  const startResize = useCallback((e) => {
    e.preventDefault();
    const currentWidth = kanbanMode ? kanbanWidth : listWidth;
    resizeDrag.current = { startX: e.clientX, startWidth: currentWidth };
    const onMove = (ev) => {
      const delta = ev.clientX - resizeDrag.current.startX;
      const next = Math.max(220, Math.min(900, resizeDrag.current.startWidth + delta));
      if (kanbanMode) {
        setKanbanWidth(next);
        localStorage.setItem('phokii-kanban-width', next);
      } else {
        setListWidth(next);
        localStorage.setItem('phokii-list-width', next);
      }
    };
    const onUp = () => {
      resizeDrag.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [kanbanMode, kanbanWidth, listWidth]);
  // Project color view mode
  // Note view mode
  const [listView, setListView] = useState(false);
  // Pho toggle feedback state
  const [phoToggleFlash, setPhoToggleFlash] = useState(new Set());
  const [goalPhoFlash, setGoalPhoFlash] = useState(new Set());
  const [projectPhoFlash, setProjectPhoFlash] = useState(new Set());

  // New create modal form states
  const [newActionText, setNewActionText] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectGoal, setNewProjectGoal] = useState('');
  const [newGoalName, setNewGoalName] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [actionSize, setActionSize] = useState('M');
  // For "Create another" checkbox and toast
  const [createAnother, setCreateAnother] = useState(false);
  const [justCreatedAction, setJustCreatedAction] = useState(null);
  
  // New keyboard shortcuts state
  const [hideEmptyProjects, setHideEmptyProjects] = useState(false);
  const [hideNonPhoObjects, setHideNonPhoObjects] = useState(false);
  const [focusedActionId, setFocusedActionId] = useState(null);
  const [lastModified, setLastModified] = useState(null);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'conflict' | 'error'

  //Time Helper
  const formatDateTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper function for authenticated API calls
  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  });

  const getAuthHeadersOnly = () => ({
    'Authorization': `Bearer ${token}`
  });

  // ──────────────────────────────────────────────────────────────────────
  // SINGLE WRITE PATH
  // dataRef mirrors every persisted array/counter and is the source of
  // truth for mutations (always current, never stale). persist() applies an
  // optimistic local update and saves the whole document in one queued
  // request. Latest snapshot always wins; saves are serialized, never dropped.
  // ──────────────────────────────────────────────────────────────────────
  const dataRef = useRef({
    goals: [], projects: [], actions: [], completedActions: [],
    deferredActions: [], archivedActions: [], trash: [], milestones: [],
    nextActionId: 1, nextProjectId: 1, nextGoalId: 1, nextMilestoneId: 1,
  });
  const saveChainRef = useRef(Promise.resolve());
  const pendingSnapshotRef = useRef(null);
  const lastBodySizeRef = useRef(0); // adaptive coalescing for image-heavy docs
  const loadedOkRef = useRef(false); // block saves until a load has succeeded

  const persist = (overrides = {}) => {
    const snap = { ...dataRef.current, ...overrides };
    dataRef.current = snap;

    // Optimistic render — only touch state that actually changed
    if ('goals' in overrides) setGoals(snap.goals);
    if ('projects' in overrides) setProjects(snap.projects);
    if ('actions' in overrides) setActions(snap.actions);
    if ('completedActions' in overrides) setCompletedActions(snap.completedActions);
    if ('deferredActions' in overrides) setDeferredActions(snap.deferredActions);
    if ('archivedActions' in overrides) setArchivedActions(snap.archivedActions);
    if ('trash' in overrides) setTrash(snap.trash);
    if ('milestones' in overrides) setMilestones(snap.milestones);
    if ('nextActionId' in overrides) setNextActionId(snap.nextActionId);
    if ('nextProjectId' in overrides) setNextProjectId(snap.nextProjectId);
    if ('nextGoalId' in overrides) setNextGoalId(snap.nextGoalId);
    if ('nextMilestoneId' in overrides) setNextMilestoneId(snap.nextMilestoneId);

    // Queue a single-document save (coalesces bursts to the latest snapshot)
    pendingSnapshotRef.current = snap;
    if (!loadedOkRef.current) {
      // Never upload state we didn't derive from a successful load — a
      // failed-load client would overwrite the real document with emptiness.
      console.error('⛔ Save blocked: no successful load in this session');
      return saveChainRef.current;
    }
    saveChainRef.current = saveChainRef.current.then(async () => {
      // Whole-doc saves get expensive once pasted images accumulate. For big
      // documents, wait a beat so rapid edits coalesce into one upload
      // instead of re-sending megabytes per keystroke burst.
      if (lastBodySizeRef.current > 2_000_000) {
        await new Promise(r => setTimeout(r, 2500));
      }
      const snapshot = pendingSnapshotRef.current;
      if (!snapshot) return;
      pendingSnapshotRef.current = null;
      setSaveStatus('saving');

      const attemptSave = async (payload) => {
        const body = JSON.stringify(payload);
        lastBodySizeRef.current = body.length;
        const response = await fetch(`${API_BASE}/api/data`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body,
        });
        if (response.status === 409) {
          setSaveStatus('conflict');
          return false;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        if (result.lastModified) {
          setLastModified(result.lastModified);
          // keep the next save's version tag current
          dataRef.current.lastModified = result.lastModified;
        }
        return true;
      };

      try {
        const ok = await attemptSave(snapshot);
        if (ok) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
        }
      } catch (err) {
        // Retry once after 3s
        await new Promise(r => setTimeout(r, 3000));
        try {
          const ok = await attemptSave(snapshot);
          if (ok) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(s => s === 'saved' ? 'idle' : s), 2000);
          }
        } catch (err2) {
          setSaveStatus('error');
          console.error('❌ Save failed after retry:', err2);
        }
      }
    });
    return saveChainRef.current;
  };

  // Back-compat alias: save the current document as-is.
  const saveData = () => persist({});

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // ⌘K — search palette, from anywhere, even mid-typing
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(o => !o);
        return;
      }
      // ⌘⇧[ / ⌘⇧] — previous/next action in zen; works even while typing in the note
      if (zenMode && e.metaKey && e.shiftKey && (e.key === '[' || e.key === ']')) {
        e.preventDefault();
        navigateZen(e.key === ']' ? 1 : -1);
        return;
      }
      // Esc in zen is one decisive press — exit, even mid-typing in the note
      // (with the filmstrip open, Esc closes that first — it's visible state)
      if (e.key === 'Escape' && zenMode && e.target.isContentEditable) {
        e.preventDefault();
        if (filmstrip) { setFilmstrip(false); return; }
        e.target.blur();
        setZenMode(false);
        return;
      }
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        // Inside input fields - handle save/cancel
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          // Find save button and click it
          const saveButton = e.target.closest('.action-notes-section')?.querySelector('button[class*="themed-button-primary"]');
          if (saveButton) saveButton.click();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          // Find cancel button and click it
          const cancelButton = e.target.closest('.action-notes-section')?.querySelector('button:not([class*="themed-button-primary"])');
          if (cancelButton) cancelButton.click();
        }
        return;
      }
      
      // Global shortcuts
      if ((e.key === 'a' || e.key === 'A') && !showAddActionModal) {
        e.preventDefault();
        setSoloFromHome(false); setSuperView(false); // leaving the room for All Actions
        setOpenFeatureId(null);
        setOpenGoalId(null);
      } else if ((e.key === 'n' || e.key === 'N') && !showAddActionModal && (!kanbanMode || minimalSolo) && !milestoneView && !ledgerView) {
        e.preventDefault();
        setShowInlineAdd(true);
      } else if ((e.key === 'l' || e.key === 'L') && !showAddActionModal) {
        e.preventDefault();
        if (e.shiftKey) {
          // ⇧L — the old List, sidebar and all (kept through the transition)
          setSoloFromHome(false); setSuperView(false);
          setZenMode(false); setKanbanMode(false);
          setMilestoneView(false); setCalendarView(false); setLedgerView(false);
        } else if (ledgerView || superView) {
          // from the wall or Goals, L is a door: the last room, as a list
          openWorkFromGoals('list');
        } else {
          setZenMode(false);
          setKanbanMode(false);
          setMilestoneView(false);
          setCalendarView(false);
          setLedgerView(false);
        }
      } else if ((e.key === 'b' || e.key === 'B') && !showAddActionModal) {
        e.preventDefault();
        if (e.shiftKey) {
          openSuperView(); // ⇧B — the deck
        } else if (ledgerView || superView) {
          openWorkFromGoals('board');
        } else {
          setZenMode(false);
          setKanbanMode(true);
          setMilestoneView(false);
          setCalendarView(false);
          setLedgerView(false);
        }
      } else if (['1','2','3','4'].includes(e.key) && !showAddActionModal) {
        // the four tabs, in the order they appear
        e.preventDefault();
        setZenMode(false);
        setSoloFromHome(false); setSuperView(false); // the tabs are the old world
        const goals = e.key === '1', list = e.key === '2', board = e.key === '3', cal = e.key === '4';
        setLedgerView(false);
        setGoalsRoomOpen(goals); // 1 = the planning room, as an overlay
        setKanbanMode(board);
        setCalendarView(cal);
        setMilestoneView(false);
        if (list) { /* list is what remains when the others are off */ }
      } else if ((e.key === 'g' || e.key === 'G') && !showAddActionModal) {
        e.preventDefault();
        // G is the planning room now (mock G, Oct 14 2026) — an overlay,
        // toggled. The old question-home (Ledger) keeps its code but no key;
        // walk-back = rebind this branch to setLedgerView(true).
        setGoalsRoomOpen(o => !o);
      } else if ((e.key === 's' || e.key === 'S') && !showAddActionModal) {
        e.preventDefault();
        setShowKeyboardShortcuts(prev => !prev);
      } else if (e.key === 'Escape') {
        // Walk back one focus depth: shortcuts → planting → brief → filmstrip → Write → pane → Focus
        e.preventDefault();
        if (confirmUnstarAll) setConfirmUnstarAll(false);
        else if (showKeyboardShortcuts) setShowKeyboardShortcuts(false);
        else if (plantingOpen) setPlantingOpen(false);
        else if (goalsRoomOpen) setGoalsRoomOpen(false);
        else if (briefOpen) setBriefOpen(false);
        else if (filmstrip) setFilmstrip(false);
        else if (zenMode) setZenMode(false);
        // The Ledger hides the pane, so it must catch Esc before the pane does
        else if (ledgerView) setLedgerView(false);
        // In the room list the note is furniture — Esc doesn't close it,
        // it walks on to the next level (the question).
        else if (selectedActionId && !roomList) setSelectedActionId(null);
        else if (focusMode) setFocusMode(false);
        // The wall: first un-solo the strips, then close back to the question
        else if (superView) {
          // the wall is the top of the ladder: Esc un-solos, then rests
          if (wallSolo.size > 0) { setWallSolo(new Set()); }
        }
        // Nav-pane deselect: Esc with nothing else open = back to All Actions (like A)
        // — unless this solo was opened from the home grid: then Esc closes
        // the loop and returns to the question (home ⇄ project).
        else if (openFeatureId !== null || openGoalId !== null) {
          setOpenFeatureId(null); setOpenGoalId(null);
          if (openFeatureId !== null && soloFromHome) {
            setSoloFromHome(false);
            openSuperView(); // home is the wall
          }
        }
      } else if ((e.key === 'w' || e.key === 'W') && !showAddActionModal) {
        e.preventDefault();
        setPlantingOpen(o => !o);
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        const newFocusMode = !focusMode;
        setFocusMode(newFocusMode);
        if (newFocusMode) setShowDeferredReview(false);
      }
      // Expand/collapse all actions with E
      // Un-pho all actions, projects, and goals with U
      else if (e.key === 'u' || e.key === 'U') {
        e.preventDefault();
        const d = dataRef.current;
        const starred = [...d.actions, ...d.completedActions, ...d.projects, ...d.goals].filter(x => x.pho);
        if (!starred.length) return;
        const goalIds = new Set();
        for (const a of d.actions.filter(x => x.pho)) {
          const proj = d.projects.find(p => p.id === a.linkId);
          if (proj && proj.goalId != null) goalIds.add(proj.goalId);
        }
        for (const g of d.goals.filter(x => x.pho)) goalIds.add(g.id);
        setConfirmUnstarAll({ stars: starred.length, goals: goalIds.size });
      }
      // Group by project with P
      else if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        setGroupByProject(prev => !prev);
      }
      // Cycle Themes with T
      else if (e.key === 't' || e.key === 'T') {
        e.preventDefault();
        const themeKeys = Object.keys(themes);
        const currentIndex = themeKeys.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % themeKeys.length;
        switchTheme(themeKeys[nextIndex]);
      }
      // Toggle hide empty projects with 0
      else if (e.key === '0') {
        e.preventDefault();
        setHideEmptyProjects(prev => !prev);
      }
      // Previous theme with comma
      else if (e.key === ',') {
        e.preventDefault();
        const themeKeys = Object.keys(themes);
        const currentIndex = themeKeys.indexOf(currentTheme);
        const prevIndex = currentIndex === 0 ? themeKeys.length - 1 : currentIndex - 1;
        switchTheme(themeKeys[prevIndex]);
      }
      // Next theme with period
      else if (e.key === '.') {
        e.preventDefault();
        const themeKeys = Object.keys(themes);
        const currentIndex = themeKeys.indexOf(currentTheme);
        const nextIndex = (currentIndex + 1) % themeKeys.length;
        switchTheme(themeKeys[nextIndex]);
      }
      // Arrow keys cycle actions in zen (only when the cursor isn't in the note)
      else if (zenMode && (e.key === 'ArrowDown' || e.key === 'ArrowRight')) {
        e.preventDefault();
        navigateZen(1);
      }
      else if (zenMode && (e.key === 'ArrowUp' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        navigateZen(-1);
      }
      // H — Home: the question. (H carried zen writing until Oct 7 2026;
      // the note still opens full screen by clicking into it. G still works.)
      else if (e.key === 'h' || e.key === 'H') {
        e.preventDefault();
        setSoloFromHome(false);
        setOpenFeatureId(null); setOpenGoalId(null);
        setKanbanMode(false);
        openSuperView(); // home is the wall
      }
      // Calendar with C
      else if (e.key === 'c' || e.key === 'C') {
        e.preventDefault();
        setCalendarView(true);
        setKanbanMode(false);
        setMilestoneView(false);
        setLedgerView(false);
      }
      // Milestone view with M
      else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        setMilestoneView(true);
        setKanbanMode(false);
        setCalendarView(false);
        setLedgerView(false);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [focusMode, zenMode, filmstrip, briefOpen, plantingOpen, showKeyboardShortcuts, selectedActionId, navigateZen, showAddActionModal, kanbanMode, milestoneView, showDeferredReview, actions, completedActions, currentTheme, switchTheme, openFeatureId, openGoalId, soloFromHome, superView, wallSolo, projects, ledgerView, goalsRoomOpen]);

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Warn before navigating away while a save is in flight
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (saveStatus === 'saving') {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [saveStatus]);

  const loadData = async () => {
    console.log('📡 Loading data...');
    setLoading(true);
    try {
      const response = await fetch(`/api/data`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401 || response.status === 403) {
        // Expired/invalid token: go to sign-in — never show an empty
        // workspace over a perfectly healthy document.
        console.warn('🔒 Session expired — returning to sign-in');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        onLogout();
        return;
      }
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Server error:', errorText);
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      const data = await response.json();
      loadedOkRef.current = true; // saves are allowed only after a real load
      console.log('✅ Data loaded:', data);
      console.log('📅 Server returned lastModified:', data.lastModified);
      console.log('🔍 Setting state with:', {
        goals: data.goals?.length || 0,
        projects: data.projects?.length || 0,
        actions: data.actions?.length || 0,
        completedActions: data.completedActions?.length || 0
      });
      
      const goals = data.goals || [];
      const projects = data.projects || [];
      const actions = data.actions || [];
      const completedActions = data.completedActions || [];
      const deferredActions = data.deferredActions || [];
      const archivedActions = data.archivedActions || [];
      // Tombstones: sweep anything past its 30-day grace on the way in
      const DAY = 24 * 60 * 60 * 1000;
      const rawTrash = data.trash || [];
      const trash = rawTrash.filter(t => Date.now() - new Date(t.deletedAt).getTime() < 30 * DAY);
      const milestones = data.milestones || [];

      // Client now owns ID generation — derive counters defensively in case
      // the server document predates them.
      const maxId = (arr) => arr.reduce((m, x) => Math.max(m, x.id || 0), 0);
      const allActionRecords = [...actions, ...completedActions, ...deferredActions, ...archivedActions];
      const nextActionId = data.nextActionId || (maxId(allActionRecords) + 1);
      const nextProjectId = data.nextProjectId || (maxId(projects) + 1);
      const nextGoalId = data.nextGoalId || (maxId(goals) + 1);
      const nextMilestoneId = data.nextMilestoneId || (maxId(milestones) + 1);

      setGoals(goals);
      setProjects(projects);
      setActions(actions);
      setCompletedActions(completedActions);
      setDeferredActions(deferredActions);
      setArchivedActions(archivedActions);
      setTrash(trash);
      setMilestones(milestones);
      setNextMilestoneId(nextMilestoneId);
      setNextActionId(nextActionId);
      setNextProjectId(nextProjectId);
      setNextGoalId(nextGoalId);
      setLastModified(data.lastModified || null);

      // Seed the write-path source of truth
      dataRef.current = {
        goals, projects, actions, completedActions, deferredActions,
        archivedActions, trash, milestones,
        nextActionId, nextProjectId, nextGoalId, nextMilestoneId,
        // Version tag for the optimistic-concurrency guard: every save sends
        // it; the server 409s if another session saved since we loaded.
        lastModified: data.lastModified || null,
      };
      console.log('📅 Set client lastModified to:', data.lastModified || null);
      // Auto-expand all active goals initially
      const activeGoalIds = (data.goals || []).filter(i => i.active).map(i => i.id);
      setExpandedGoals(new Set(activeGoalIds));
      
    } catch (error) {
      console.error('❌ Error loading data:', error);
      alert(`Failed to connect to server: ${error.message}. Check if backend is running on port 5001.`);
    } finally {
      setLoading(false);
    }
  };

  /* ── Milestone CRUD ── */
  const createMilestone = (data) => {
    const d = dataRef.current;
    const id = d.nextMilestoneId;
    persist({
      milestones: [...d.milestones, { id, createdDate: new Date().toISOString(), ...data }],
      nextMilestoneId: id + 1,
    });
  };

  const updateMilestone = (updated) => {
    const d = dataRef.current;
    persist({ milestones: d.milestones.map(m => m.id === updated.id ? updated : m) });
  };

  const deleteMilestone = (id) => {
    const d = dataRef.current;
    persist({ milestones: d.milestones.filter(m => m.id !== id) });
  };

  /* ── Inline sidebar add helpers ── */
  const addProjectInline = (name, goalId) => {
    const d = dataRef.current;
    const id = d.nextProjectId;
    const newProject = { id, name, goalId: goalId || null, active: true, pho: false, created_at: new Date().toISOString() };
    persist({ projects: [...d.projects, newProject], nextProjectId: id + 1 });
  };

  const addGoalInline = (name) => {
    const d = dataRef.current;
    const id = d.nextGoalId;
    const newGoal = { id, name, active: true, pho: false, created_at: new Date().toISOString() };
    persist({ goals: [...d.goals, newGoal], nextGoalId: id + 1 });
    setExpandedGoals(prev => new Set(prev).add(id));
  };

  // Build a fresh action record with a client-owned ID.
  const makeAction = (id, fields) => ({
    id,
    text: '',
    linkType: 'unlinked',
    linkId: null,
    status: 'todo',
    completed: false,
    pho: false,
    checklist: [],
    notes: '',
    nextStep: '',
    createdDate: new Date().toISOString(),
    // Manual-order rank: new actions land at the top of the hand-arranged list
    order: dataRef.current.actions.length
      ? Math.min(...dataRef.current.actions.map(g => g.order ?? g.id)) - 1
      : id,
    ...fields,
  });

  const addAction = (actionText = newAction, linkType = 'unlinked', linkId = null, urgent = false, priority = 'low') => {
    if (!actionText.trim()) return;
    const d = dataRef.current;
    const id = d.nextActionId;
    const newActionObj = makeAction(id, {
      text: actionText.trim(),
      linkType: linkType === 'project' ? 'project' : 'unlinked',
      linkId, urgent, priority,
    });
    persist({ actions: [...d.actions, newActionObj], nextActionId: id + 1 });
    setNewAction('');
    setShowAddActionModal(false);
    resetCreateModal();
  };

  const addActionFromModal = () => {
    if (!newActionText.trim()) return;
    const d = dataRef.current;
    // Fall back to the sidebar's open project when the dropdown hasn't been touched
    const effectiveProjId = selectedProjectId
      || (openFeatureId && openFeatureId !== 'unassigned' ? String(openFeatureId) : '');
    const linkType = effectiveProjId ? 'project' : 'unlinked';
    const linkId = effectiveProjId ? parseInt(effectiveProjId) : null;

    // Inherit pho from linked project
    let inheritedPho = false;
    if (linkType === 'project' && linkId) {
      const linkedProject = d.projects.find(p => p.id === linkId);
      if (linkedProject) inheritedPho = linkedProject.pho || false;
    }

    const id = d.nextActionId;
    const newActionObj = makeAction(id, { text: newActionText.trim(), linkType, linkId, pho: inheritedPho });
    persist({ actions: [...d.actions, newActionObj], nextActionId: id + 1 });
    setJustCreatedAction(newActionObj);
    if (!createAnother) {
      resetCreateModal();
    } else {
      setNewActionText('');
    }
  };
  // Toast confirmation after creating a action
  useEffect(() => {
    if (justCreatedAction) {
      const toast = document.createElement('div');
      toast.textContent = `✅ Created: ${justCreatedAction.text}`;
      toast.className = 'fixed bottom-6 right-6 bg-green-600 text-white px-4 py-2 rounded shadow-lg z-50';
      document.body.appendChild(toast);
      const timeout = setTimeout(() => {
        document.body.removeChild(toast);
        setJustCreatedAction(null);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [justCreatedAction]);

  const addProject = async () => {
    if (newProjectName.trim()) {
      addProjectInline(newProjectName.trim(), newProjectGoal ? parseInt(newProjectGoal) : null);
      resetCreateModal();
    }
  };

  const addGoal = async () => {
    if (newGoalName.trim()) {
      addGoalInline(newGoalName.trim());
      resetCreateModal();
    }
  };

  const resetCreateModal = () => {
    setShowCreateModal(false);
    setNewActionText('');
    setNewProjectName('');
    setNewProjectGoal('');
    setNewGoalName('');
    setSelectedProjectId('');
    setActionSize('M');
    setCreateModalTab('action');
    setBulkImportText('');
    setImportTargetProject('');
  };

  const importBulkActions = () => {
    if (!bulkImportText.trim()) return;

    const actionLines = bulkImportText.trim().split('\n').map(l => l.trim()).filter(Boolean);
    const targetProjectId = importTargetProject ? parseInt(importTargetProject) : null;
    const linkType = targetProjectId ? 'project' : 'unlinked';
    const d = dataRef.current;

    // Inherit pho from linked project
    let inheritedPho = false;
    if (targetProjectId) {
      const linkedProject = d.projects.find(p => p.id === targetProjectId);
      if (linkedProject) inheritedPho = linkedProject.pho || false;
    }

    let id = d.nextActionId;
    const newActions = actionLines.map(text => makeAction(id++, {
      text, linkType, linkId: targetProjectId, size: 'M', pho: inheritedPho,
    }));

    if (newActions.length > 0) {
      persist({ actions: [...d.actions, ...newActions], nextActionId: id });
      alert(`✅ Successfully imported ${newActions.length} action${newActions.length > 1 ? 's' : ''}!`);
      resetCreateModal();
    }
  };

  const [aiPromptCopied, setAiPromptCopied] = useState(false);
  const copyAiPrompt = async () => {
    try {
      await navigator.clipboard.writeText(AI_PROMPT);
      setAiPromptCopied(true);
      setTimeout(() => setAiPromptCopied(false), 2000);
    } catch { alert('Could not access the clipboard — copy docs/phokii-ai-prompt.md instead.'); }
  };

  const exportBackup = () => {
    const d = dataRef.current;
    const backup = {
      format: 'phokii-backup-v1',
      exportedAt: new Date().toISOString(),
      user: user?.email,
      goals: d.goals, projects: d.projects,
      actions: d.actions, completedActions: d.completedActions,
      deferredActions: d.deferredActions, archivedActions: d.archivedActions,
      trash: d.trash,
      milestones: d.milestones || milestones,
      nextActionId: d.nextActionId, nextProjectId: d.nextProjectId,
      nextGoalId: d.nextGoalId, nextMilestoneId: d.nextMilestoneId,
    };
    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `phokii-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const loadStarterProject = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/load-starter`, { method: 'POST', headers: getAuthHeadersOnly() });
      if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      await loadData();
    } catch (e) { alert('Could not load starter: ' + e.message); }
  };

  const runProjectImport = async () => {
    setProjectImportError('');
    setProjectImportResult(null);
    let parsed;
    try {
      // tolerate AI output wrapped in ```json fences
      const cleaned = projectImportJson.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
      parsed = JSON.parse(cleaned);
    } catch (e) {
      setProjectImportError('Invalid JSON — ' + e.message);
      return;
    }
    if (!Array.isArray(parsed)) {
      setProjectImportError('Expected a JSON array [ ... ]');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeadersOnly() },
        body: JSON.stringify(parsed),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Import failed');
      const result = await res.json();
      setProjectImportResult(result);
      await loadData();
    } catch (e) {
      setProjectImportError('Import failed — ' + e.message);
    }
  };

  const exportData = (type) => {
    try {
      let dataToExport = [];
      const allActions = [...actions, ...completedActions, ...archivedActions];
      
      if (type === 'active') {
        dataToExport = actions;
      } else if (type === 'completed') {
        dataToExport = [...completedActions, ...archivedActions];
      } else {
        dataToExport = allActions;
      }

      // Create CSV content
      const headers = [
        'Action Text',
        'Project',
        'Goal', 
        'Status',
        'Size',
        'Pho',
        'Created Date',
        'Completed Date',
        'Notes'
      ];

      const rows = dataToExport.map(action => {
        const project = projects.find(p => p.id === action.linkId && action.linkType === 'project');
        const goal = project ? goals.find(i => i.id === project.goalId) : null;
        
        return [
          action.text || '',
          project?.name || (action.linkType === 'unlinked' ? 'Unassigned' : ''),
          goal?.name || '',
          action.completed ? 'Completed' : 'Active',
          action.size || 'M',
          action.pho ? 'Yes' : 'No',
          action.createdDate ? new Date(action.createdDate).toLocaleDateString() : '',
          action.completedDate ? new Date(action.completedDate).toLocaleDateString() : '',
          action.notes || ''
        ];
      });

      // Convert to CSV
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(field => 
          typeof field === 'string' && (field.includes(',') || field.includes('"')) 
            ? `"${field.replace(/"/g, '""')}"` 
            : field
        ).join(','))
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `daily-focus-${type}-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      alert(`✅ ${type.charAt(0).toUpperCase() + type.slice(1)} data exported successfully!`);
    } catch (error) {
      console.error('❌ Error exporting data:', error);
      alert(`Failed to export data: ${error.message}`);
    }
  };

  // Apply updates to a action wherever it lives, then re-bucket it into the
  // array its status implies (active / completed / archived / deferred).
  const updateAction = (actionId, updates) => {
    const d = dataRef.current;
    const buckets = {
      actions: [...d.actions],
      completedActions: [...d.completedActions],
      archivedActions: [...d.archivedActions],
      deferredActions: [...d.deferredActions],
    };

    let found = null;
    for (const name of ['actions', 'completedActions', 'archivedActions', 'deferredActions']) {
      const idx = buckets[name].findIndex(g => g.id === actionId);
      if (idx !== -1) {
        found = { ...buckets[name][idx], ...updates };
        buckets[name].splice(idx, 1);
        break;
      }
    }
    if (!found) return;

    // Stamp "last worked" for any real edit (not a bare star toggle) so we
    // can resume the most recently touched action on cold open.
    const changedKeys = Object.keys(updates);
    if (!(changedKeys.length === 1 && changedKeys[0] === 'pho')) {
      found.lastTouchedAt = new Date().toISOString();
    }

    if (found.completed && !found.completedDate) {
      found.completedDate = new Date().toISOString();
    }

    let target = 'actions';
    if (found.completed) target = 'completedActions';
    else if (found.status === 'archived') target = 'archivedActions';
    else if (found.status === 'deferred') target = 'deferredActions';
    buckets[target].push(found);

    return persist(buckets);
  };

  const getProjectPriorityHint = (project, projects, goals) => {
    const activeProjects = projects.filter(p => p.active);
    const projectIndex = activeProjects.findIndex(p => p.id === project.id);
    
    if (projectIndex === 0) return '(🔥 High Priority)';
    if (projectIndex === 1) {
      const goal = goals.find(i => i.id === project.goalId && i.active);
      if (goal && goals.filter(i => i.active).findIndex(i => i.id === goal.id) === 0) {
        return '(⚡ Medium Priority)';
      }
    }
    return '(⚪ Low Priority)';
  };

  const getProjectPrioritySuggestion = (project, projects, goals) => {
    if (!project) return 'low';
    
    const activeProjects = projects.filter(p => p.active);
    const projectIndex = activeProjects.findIndex(p => p.id === project.id);
    
    if (projectIndex === 0) return 'high';
    if (projectIndex === 1) {
      const goal = goals.find(i => i.id === project.goalId && i.active);
      if (goal && goals.filter(i => i.active).findIndex(i => i.id === goal.id) === 0) {
        return 'medium';
      }
    }
    return 'low';
  };

  const batchAssignActions = (assignments) => {
    const d = dataRef.current;
    const byId = new Map(assignments.map(a => [a.actionId, a]));
    const apply = (g) => byId.has(g.id)
      ? { ...g, linkType: byId.get(g.id).linkType, linkId: byId.get(g.id).linkId }
      : g;
    persist({ actions: d.actions.map(apply), completedActions: d.completedActions.map(apply) });
    setShowBatchAssignModal(false);
  };

  const setActionStatus = (actionId, status) => {
    if (status === 'done') {
      const d = dataRef.current;
      const action = [...d.actions, ...d.completedActions].find(g => g.id === actionId);
      if (action && !action.completed) {
        setCompletionModal({ actionId, actionText: action.text });
        setCompletionResolution('done');
        setCompletionComment('');
        return;
      }
    }
    applyActionStatus(actionId, status);
  };

  const applyActionStatus = (actionId, status) => {
    updateAction(actionId, { status, completed: status === 'done' });
  };

  const toggleActionComplete = (actionId) => {
    const d = dataRef.current;
    const action = [...d.actions, ...d.completedActions].find(g => g.id === actionId);
    if (!action) return;
    if (!action.completed) {
      // Opening — show confirmation modal
      setCompletionModal({ actionId, actionText: action.text });
      setCompletionResolution('done');
      setCompletionComment('');
    } else {
      // Re-opening — no modal needed, just reactivate
      confirmReopen(actionId);
    }
  };

  const confirmReopen = (actionId) => {
    updateAction(actionId, { completed: false, status: 'todo', resolutionType: null, resolutionComment: null, completedDate: null });
  };

  // The rack's Close: the receipt travels inline — no modal detour
  const completeWithReceipt = (actionId, receipt) => {
    updateAction(actionId, {
      completed: true,
      status: 'done',
      completedDate: new Date().toISOString(),
      resolutionType: 'done',
      resolutionComment: (receipt || '').trim() || null,
    });
    if (selectedActionId === actionId) setSelectedActionId(null);
  };

  const confirmComplete = async () => {
    const { actionId } = completionModal;
    setCompletionModal(null);
    setCompletingAction(actionId);
    setTimeout(() => setCompletingAction(null), 800);
    updateAction(actionId, {
      completed: true,
      status: 'done',
      completedDate: new Date().toISOString(),
      resolutionType: completionResolution,
      resolutionComment: completionComment.trim() || null,
    });
    if (selectedActionId === actionId) setSelectedActionId(null);
  };

  const deferAction = (actionId) => {
    setDeferringActionId(actionId);
    setShowDatePicker(true);
  };

  const confirmDefer = (deferDate) => {
    if (deferDate && deferringActionId) {
      const d = dataRef.current;
      const action = d.actions.find(g => g.id === deferringActionId);
      if (action) {
        persist({
          actions: d.actions.filter(g => g.id !== deferringActionId),
          deferredActions: [...d.deferredActions, { ...action, status: 'deferred', deferToDate: deferDate }],
        });
      }
    }
    setShowDatePicker(false);
    setDeferringActionId(null);
  };

  const promoteAction = (actionId) => {
    const d = dataRef.current;
    const action = d.deferredActions.find(g => g.id === actionId);
    if (!action) return;
    const { deferToDate, ...rest } = action;
    persist({
      deferredActions: d.deferredActions.filter(g => g.id !== actionId),
      actions: [...d.actions, { ...rest, status: 'todo' }],
    });
  };

  // Deletes are tombstones: the object (and, for containers, its children)
  // moves to `trash` for 30 days — restorable from Recently deleted, with an
  // immediate Undo toast. Confirms live HERE; call sites must not re-confirm.
  const offerUndo = (message, restoreOverrides) => {
    setUndoToast({
      message,
      restore: () => { persist(restoreOverrides); setUndoToast(null); },
    });
    setTimeout(() => setUndoToast(cur => (cur && cur.message === message ? null : cur)), 6000);
  };

  // ── Planting (goals-first): create bare, or land a whole plan additively ──
  const plantGoalBare = (name) => {
    const d = dataRef.current;
    if (!name || !name.trim()) return;
    const id = d.nextGoalId;
    const goal = { id, name: name.trim(), active: true, pho: false, created_at: new Date().toISOString() };
    persist({ goals: [...d.goals, goal], nextGoalId: id + 1 });
    setPlantingOpen(false);
    openLedgerAt('goal', id);
  };

  // Client-side mirror of the server's applyBundles: additive, matches goals
  // and projects by name, never overwrites — and undoable as one unit.
  const plantBundles = (bundles, opts) => {
    const d = dataRef.current;
    const snapshot = {
      goals: d.goals, projects: d.projects, actions: d.actions,
      nextGoalId: d.nextGoalId, nextProjectId: d.nextProjectId, nextActionId: d.nextActionId,
    };
    let goals = [...d.goals], projects = [...d.projects], actions = [...d.actions];
    let nextGoalId = d.nextGoalId, nextProjectId = d.nextProjectId, nextActionId = d.nextActionId;
    // Plan order reads top-to-bottom: seat all new actions just above the old top
    const orderBase = (d.actions.length ? Math.min(...d.actions.map(g => g.order ?? g.id)) : 1) - 1;
    let seq = 0, addedProjects = 0, addedActions = 0, firstGoalId = null, firstGoalName = null;
    const createdGoalIds = [];   // only goals born in THIS plant may be set aside
    const civil = (s) => (typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)) ? s : undefined;

    for (const b of bundles || []) {
      if (!b || !b.project || !Array.isArray(b.actions)) continue;
      const goalName = b.goal || b.objective;
      const goalNotes = b.goalNotes || b.objectiveNotes;
      let goalId = null;
      if (goalName) {
        let g = goals.find(x => x.name.toLowerCase() === goalName.toLowerCase());
        if (!g) {
          g = { id: nextGoalId++, name: goalName, active: true, pho: false, created_at: new Date().toISOString() };
          goals = [...goals, g];
          createdGoalIds.push(g.id);
        }
        if (goalNotes && !g.notes) goals = goals.map(x => (x.id === g.id ? { ...x, notes: goalNotes } : x));
        goalId = g.id;
        if (firstGoalId === null) { firstGoalId = g.id; firstGoalName = g.name; }
      }
      // Scoped to this goal, never workspace-wide. A project name only means
      // anything inside its goal: fifteen pairs of library goals share one
      // (two different plans both open with "Find out where the money actually
      // goes"). Matching globally handed the second plan's actions to the first
      // goal's project and left the new book a project short, with nothing said.
      let p = projects.find(x => x.goalId === goalId && x.name.toLowerCase() === b.project.toLowerCase());
      if (!p) {
        p = { id: nextProjectId++, name: b.project, goalId, active: true, pho: false, created_at: new Date().toISOString() };
        projects = [...projects, p];
        addedProjects++;
      }
      if (b.projectNotes && !p.notes) projects = projects.map(x => (x.id === p.id ? { ...x, notes: b.projectNotes } : x));
      for (const a of b.actions) {
        if (!a || !a.title) continue;
        actions = [...actions, {
          id: nextActionId++,
          text: a.title,
          notes: a.notes || '',
          nextStep: a.nextStep || '',
          status: a.status === 'doing' ? 'doing' : 'todo',
          pho: false, // suggested, never starred — stars are the user's daily promise
          completed: false,
          linkType: 'project',
          linkId: p.id,
          createdDate: new Date().toISOString(),
          order: orderBase + (seq++) * 0.0001,
          ...(civil(a.doDate) ? { doDate: civil(a.doDate) } : {}),
          ...(civil(a.dueDate) ? { dueDate: civil(a.dueDate) } : {}),
          checklist: (a.checklist || []).map((item, idx) => ({
            id: idx + 1,
            text: typeof item === 'string' ? item : item.text,
            completed: typeof item === 'object' ? (item.done || false) : false,
          })),
        }];
        addedActions++;
      }
    }
    if (!addedActions && !addedProjects) return null;
    // "Take it, set it aside": only goals BORN in this plant may arrive
    // resting — merging into a goal already standing must never yank it off
    // the shelf. Set-aside is user-only, always.
    if (opts && opts.setAside && createdGoalIds.length) {
      goals = goals.map(g => (createdGoalIds.includes(g.id) ? { ...g, setAside: true } : g));
    }
    persist({ goals, projects, actions, nextGoalId, nextProjectId, nextActionId });
    offerUndo(`Planted "${firstGoalName || 'plan'}" — ${addedProjects} project${addedProjects === 1 ? '' : 's'}, ${addedActions} action${addedActions === 1 ? '' : 's'}`, snapshot);
    return { goalId: firstGoalId, goalName: firstGoalName, projects: addedProjects, actions: addedActions, setAside: !!(opts && opts.setAside && createdGoalIds.length) };
  };

  // The implementation intention becomes the charter's first sentence
  const savePlantingIntention = (goalId, text) => {
    const d = dataRef.current;
    persist({
      goals: d.goals.map(g => (g.id === goalId ? { ...g, notes: `> ${text}\n\n${g.notes || ''}` } : g)),
    });
  };

  const deleteAction = (actionId) => {
    const d = dataRef.current;
    const buckets = ['actions', 'completedActions', 'archivedActions', 'deferredActions'];
    const from = buckets.find(b => d[b].some(g => g.id === actionId));
    if (!from) return false;
    const action = d[from].find(g => g.id === actionId);
    // No browser confirm (UX-8): the undo toast and the 30-day trash are the
    // guard. Deleting acts at once and offers the way back in the same breath.
    const restore = { [from]: d[from], trash: d.trash };
    persist({
      [from]: d[from].filter(g => g.id !== actionId),
      trash: [...d.trash, { kind: 'action', deletedAt: new Date().toISOString(), from, object: action }],
    });
    offerUndo(`Deleted "${action.text.slice(0, 40)}"`, restore);
    return true;
  };

  // No confirms on any delete (UX-8) — the old dialogs even said "can't be
  // undone", which was false twice over: Undo restores the cascade whole, and
  // Recently deleted holds it for 30 days. The toast names what went.
  const deleteProject = (projectId) => {
    const d = dataRef.current;
    const proj = d.projects.find(p => p.id === projectId);
    const linked = (g) => g.linkType === 'project' && g.linkId === projectId;
    const n = d.actions.filter(linked).length + d.completedActions.filter(linked).length;
    const restore = { projects: d.projects, actions: d.actions, completedActions: d.completedActions, trash: d.trash };
    persist({
      projects: d.projects.filter(p => p.id !== projectId),
      actions: d.actions.filter(g => !linked(g)),
      completedActions: d.completedActions.filter(g => !linked(g)),
      trash: [...d.trash, {
        kind: 'project', deletedAt: new Date().toISOString(), object: proj,
        children: { actions: d.actions.filter(linked), completedActions: d.completedActions.filter(linked) },
      }],
    });
    offerUndo(`Deleted project "${(proj?.name || '').slice(0, 40)}"${n > 0 ? ` and ${n} action${n === 1 ? '' : 's'}` : ''}`, restore);
    return true;
  };

  const deleteGoal = (goalId) => {
    const d = dataRef.current;
    const ini = d.goals.find(i => i.id === goalId);
    const projectIds = d.projects.filter(p => p.goalId === goalId).map(p => p.id);
    const linked = (g) => g.linkType === 'project' && projectIds.includes(g.linkId);
    const nProjects = projectIds.length;
    const nActions = d.actions.filter(linked).length + d.completedActions.filter(linked).length;
    const contents = [
      nProjects > 0 && `${nProjects} project${nProjects === 1 ? '' : 's'}`,
      nActions > 0 && `${nActions} action${nActions === 1 ? '' : 's'}`,
    ].filter(Boolean).join(' and ');
    const restore = { goals: d.goals, projects: d.projects, actions: d.actions, completedActions: d.completedActions, trash: d.trash };
    persist({
      goals: d.goals.filter(i => i.id !== goalId),
      projects: d.projects.filter(p => p.goalId !== goalId),
      actions: d.actions.filter(g => !linked(g)),
      completedActions: d.completedActions.filter(g => !linked(g)),
      trash: [...d.trash, {
        kind: 'goal', deletedAt: new Date().toISOString(), object: ini,
        children: {
          projects: d.projects.filter(p => p.goalId === goalId),
          actions: d.actions.filter(linked),
          completedActions: d.completedActions.filter(linked),
        },
      }],
    });
    offerUndo(`Deleted goal "${(ini?.name || '').slice(0, 40)}"`, restore);
    return true;
  };

  // Recently-deleted operations
  const restoreFromTrash = (idx) => {
    const d = dataRef.current;
    const t = d.trash[idx];
    if (!t) return;
    const rest = d.trash.filter((_, i) => i !== idx);
    if (t.kind === 'action') {
      persist({ [t.from || 'actions']: [...d[t.from || 'actions'], t.object], trash: rest });
    } else if (t.kind === 'project') {
      persist({
        projects: [...d.projects, t.object],
        actions: [...d.actions, ...(t.children?.actions || [])],
        completedActions: [...d.completedActions, ...(t.children?.completedActions || [])],
        trash: rest,
      });
    } else if (t.kind === 'goal') {
      persist({
        goals: [...d.goals, t.object],
        projects: [...d.projects, ...(t.children?.projects || [])],
        actions: [...d.actions, ...(t.children?.actions || [])],
        completedActions: [...d.completedActions, ...(t.children?.completedActions || [])],
        trash: rest,
      });
    }
  };

  const deleteForever = (idx) => {
    const d = dataRef.current;
    const t = d.trash[idx];
    if (!t) return;
    const label = t.object?.text || t.object?.name || 'this item';
    if (!window.confirm(`Permanently delete "${label}"? This one really can't be undone.`)) return;
    persist({ trash: d.trash.filter((_, i) => i !== idx) });
  };

  const openChecklistModal = (actionId) => {
    setChecklistTargetId(actionId);
    setNewChecklistText('');
    setShowChecklistModal(true);
  };

  const confirmAddChecklistItem = () => {
    if (newChecklistText?.trim() && checklistTargetId) {
      const d = dataRef.current;
      const action = [...d.actions, ...d.completedActions].find(g => g.id === checklistTargetId);
      if (action) {
        const newChecklistItem = {
          id: Math.max(...(action.checklist?.map(a => a.id) || [0]), 0) + 1,
          text: newChecklistText.trim(),
          completed: false
        };
        updateAction(checklistTargetId, { checklist: [...(action.checklist || []), newChecklistItem] });
      }
      setShowChecklistModal(false);
      setNewChecklistText('');
      setChecklistTargetId(null);
    }
  };

  const toggleGoalExpanded = (goalId) => {
    const newExpanded = new Set(expandedGoals);
    if (newExpanded.has(goalId)) {
      newExpanded.delete(goalId);
    } else {
      newExpanded.add(goalId);
    }
    setExpandedGoals(newExpanded);
  };

  const getPriorityLevel = (action) => {
    if (action.urgent) return 'urgent';
    
    if (action.priority) {
      return action.priority;
    }
    
    if (action.linkType === 'project') {
      const project = projects.find(p => p.id === action.linkId && p.active);
      if (project) {
        const activeProjects = projects.filter(p => p.active);
        const projectIndex = activeProjects.findIndex(p => p.id === action.linkId);
        
        if (projectIndex === 0) return 'high';
        if (projectIndex === 1) {
          const goal = goals.find(i => i.id === project.goalId && i.active);
          if (goal && goals.filter(i => i.active).findIndex(i => i.id === goal.id) === 0) {
            return 'medium';
          }
        }
      }
    } else if (action.linkType === 'unlinked') {
      return 'unlinked';
    }
    
    return 'low';
  };

  const getPriorityLabel = (level) => {
    switch (level) {
      case 'urgent': return 'URGENT';
      case 'high': return 'HIGH';
      case 'medium': return 'MED';
      case 'low': return 'LOW';
      case 'unlinked': return 'UNLINKED';
      default: return 'LOW';
    }
  };

  const getPriorityColor = (level) => {
    switch (level) {
      case 'urgent': return 'bg-red-500 text-white border-red-600';
      case 'high': return 'bg-orange-500 text-white border-orange-600';
      case 'medium': return 'bg-yellow-500 text-white border-yellow-600';
      case 'low': return 'bg-gray-400 text-white border-gray-500';
      case 'unlinked': return 'bg-blue-500 text-white border-blue-600';
      default: return 'bg-gray-400 text-white border-gray-500';
    }
  };

  const getStrategicAlignmentColor = (percentage) => {
    if (percentage >= 67) return 'themed-success';
    if (percentage >= 34) return 'text-yellow-600';
    return 'themed-error';
  };

  // Accept isExpanded as second argument - Smart shortened display with visual cues
  const getActionLinkDisplay = (action, isExpanded) => {
    if (action.linkType === 'project') {
      const project = projects.find(p => p.id === action.linkId);
      const goal = project ? goals.find(i => i.id === project.goalId) : null;
      
      if (project && goal) {
        const status = project?.active ? '' : ' [Archived]';
        const fullText = `${goal.name} → ${project.name}${status}`;
        
        // Smart shortened text - show just project name for cleaner look
        const displayText = isExpanded ? `${project.name}${status}` : '';
        
        return {
          text: displayText.length > 40 ? displayText.substring(0, 37) + '...' : displayText,
          fullText: fullText,
          color: 'themed-text-muted',
          type: 'project',
          dotColor: getProjectColorById(project.id),
          goalName: goal.name
        };
      } else if (project) {
        const status = project?.active ? '' : ' [Archived]';
        const displayText = isExpanded ? `${project.name}${status}` : `${project.name}${status}`;
        return {
          text: displayText.length > 40 ? displayText.substring(0, 37) + '...' : displayText,
          fullText: `${project.name} → Unlinked${status}`,
          color: 'themed-text-muted',
          type: 'unlinked-project',
          dotColor: '#9ca3af'
        };
      } else {
        return { 
          text: 'Unknown Project', 
          fullText: 'Unknown Project', 
          color: 'themed-text-muted',
          type: 'unknown',
          dotColor: '#ef4444'
        };
      }
    } else if (action.linkType === 'unlinked') {
      return { 
        text: 'Unlinked', 
        fullText: 'Unlinked - Ready to assign', 
        color: 'themed-text-muted',
        type: 'unlinked',
        dotColor: '#6b7280'
      };
    }
    return { 
      text: 'Unknown', 
      fullText: 'Unknown', 
      color: 'themed-text-muted',
      type: 'unknown',
      dotColor: '#ef4444'
    };
  };

  const getGoalColorById = (goalId) => {
    const activeGoals = goals.filter(i => i.active);
    const index = activeGoals.findIndex(i => i.id === goalId);
    if (index === -1) return '';
    const hue = (index * 47) % 360;
    return `hsla(${hue}, 70%, 85%, 0.3)`; // pastel tint with transparency
  };
  // Project color helper
  const getProjectColorById = (projectId) => {
    const activeProjects = projects.filter(p => p.active);
    const index = activeProjects.findIndex(p => p.id === projectId);
    if (index === -1) return '';
    const hue = (index * 71) % 360;
    return `hsla(${hue}, 60%, 80%, 0.3)`;
  };

  const getActionCountForProject = (projectId) => {
    return actions.filter(g => g.linkType === 'project' && g.linkId === projectId).length;
  };

  // Manual sorting functions
  const moveActionUp = async (actionId) => {
    const action = actions.find(g => g.id === actionId);
    if (!action) return;
    
    const priority = getPriorityLevel(action);
    const samePriorityActions = actions.filter(g => getPriorityLevel(g) === priority);
    const currentIndex = samePriorityActions.findIndex(g => g.id === actionId);
    
    if (currentIndex > 0) {
      console.log(`Moving action ${actionId} up within ${priority} priority`);
      
      const prevAction = samePriorityActions[currentIndex - 1];
      const currentSortOrder = action.sortOrder || currentIndex;
      const prevSortOrder = prevAction.sortOrder || (currentIndex - 1);
      
      await updateAction(actionId, { sortOrder: prevSortOrder });
      await updateAction(prevAction.id, { sortOrder: currentSortOrder });
    }
  };

  const moveActionDown = async (actionId) => {
    const action = actions.find(g => g.id === actionId);
    if (!action) return;
    
    const priority = getPriorityLevel(action);
    const samePriorityActions = actions.filter(g => getPriorityLevel(g) === priority);
    const currentIndex = samePriorityActions.findIndex(g => g.id === actionId);
    
    if (currentIndex < samePriorityActions.length - 1) {
      console.log(`Moving action ${actionId} down within ${priority} priority`);
      
      const nextAction = samePriorityActions[currentIndex + 1];
      const currentSortOrder = action.sortOrder || currentIndex;
      const nextSortOrder = nextAction.sortOrder || (currentIndex + 1);
      
      await updateAction(actionId, { sortOrder: nextSortOrder });
      await updateAction(nextAction.id, { sortOrder: currentSortOrder });
    }
  };

  const canMoveDown = (actionId) => {
    const action = actions.find(g => g.id === actionId);
    if (!action) return false;
    
    const priority = getPriorityLevel(action);
    const samePriorityActions = actions.filter(g => getPriorityLevel(g) === priority);
    const currentIndex = samePriorityActions.findIndex(g => g.id === actionId);
    
    return currentIndex < samePriorityActions.length - 1 && samePriorityActions.length > 1;
  };

  // Helper functions for updating goals and projects
  const updateGoal = (id, updates) => {
    const isPho = Object.prototype.hasOwnProperty.call(updates, 'pho');
    if (isPho) {
      setGoalPhoFlash(prev => new Set(prev).add(id));
      setTimeout(() => {
        setGoalPhoFlash(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 600);
    }

    const d = dataRef.current;
    const overrides = {
      goals: d.goals.map(init => init.id === id ? { ...init, ...updates } : init),
    };

    // Cascade pho down to this goal's projects and their actions
    if (isPho) {
      const newPho = updates.pho;
      const projectIds = d.projects.filter(p => p.goalId === id).map(p => p.id);
      const cascade = (g) => g.linkType === 'project' && projectIds.includes(g.linkId)
        ? { ...g, pho: newPho } : g;
      overrides.projects = d.projects.map(p => p.goalId === id ? { ...p, pho: newPho } : p);
      overrides.actions = d.actions.map(cascade);
      overrides.completedActions = d.completedActions.map(cascade);
    }

    persist(overrides);
  };

  const updateProject = (id, updates) => {
    const isPho = Object.prototype.hasOwnProperty.call(updates, 'pho');
    if (isPho) {
      setProjectPhoFlash(prev => new Set(prev).add(id));
      setTimeout(() => {
        setProjectPhoFlash(prev => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
      }, 600);
    }

    const d = dataRef.current;
    const overrides = {
      projects: d.projects.map(proj => proj.id === id ? { ...proj, ...updates } : proj),
    };

    // Cascade pho down to this project's actions
    if (isPho) {
      const newPho = updates.pho;
      const cascade = (g) => g.linkType === 'project' && g.linkId === id
        ? { ...g, pho: newPho } : g;
      overrides.actions = d.actions.map(cascade);
      overrides.completedActions = d.completedActions.map(cascade);
    }

    persist(overrides);
  };

  const unlinkedActions = actions.filter(g => g.linkType === 'unlinked');
  
  // Filter functions for new shortcuts
  const shouldHideProject = (project) => {
    if (!hideEmptyProjects) return false;
    return getActionCountForProject(project.id) === 0;
  };
  
  const shouldHideObject = (obj) => {
    if (!hideNonPhoObjects) return false;
    return !obj.pho;
  };
  
  const filteredProjects = projects.filter(p => !shouldHideProject(p) && !shouldHideObject(p));
  const filteredGoals = goals.filter(i => !shouldHideObject(i));
  const filteredActions = actions.filter(g => !shouldHideObject(g));
  const filteredCompletedActions = completedActions.filter(g => !shouldHideObject(g));
  
  // Count hidden projects for UI message
  const hiddenProjectCount = projects.filter(p => shouldHideProject(p)).length;
  const todayDeferredActions = deferredActions.filter(g => {
    const today = new Date().toISOString().split('T')[0];
    return g.deferToDate === today;
  });

  // Calculate alignment based on pho actions
  const phoActionsOpen = actions.filter(g => g.pho === true);
  const phoActionsCompleted = completedActions.filter(g => g.pho === true);
  const totalPhoActions = phoActionsOpen.length + phoActionsCompleted.length;
  const totalActiveActions = actions.length;
  const totalCompletedActions = completedActions.length;

  // Sort lens. Manual (your hand-arranged order) is the default truth;
  // the other sorts are temporary ways of looking at it.
  const [sortOption, setSortOption] = useState(() => localStorage.getItem('phokii-sort') || 'manual');
  useEffect(() => { localStorage.setItem('phokii-sort', sortOption); }, [sortOption]);
  const [dragOverRow, setDragOverRow] = useState(null); // { id, before } — drop indicator

  // Fractional re-ranking: dropping writes ONE order value, nothing renumbers.
  const reorderAction = (draggedId, targetId, before) => {
    if (draggedId === targetId) { setDragOverRow(null); return; }
    const d = dataRef.current;
    const ord = (g) => g.order ?? g.id;
    const seq = d.actions.filter(g => !g.completed).sort((a, b) => ord(a) - ord(b));
    const ti = seq.findIndex(g => g.id === targetId);
    if (ti === -1) { setDragOverRow(null); return; }
    const prev = before ? seq[ti - 1] : seq[ti];
    const next = before ? seq[ti] : seq[ti + 1];
    if ((prev && prev.id === draggedId) || (next && next.id === draggedId)) { setDragOverRow(null); return; }
    let newOrder;
    if (prev && next) newOrder = (ord(prev) + ord(next)) / 2;
    else if (next) newOrder = ord(next) - 1;
    else if (prev) newOrder = ord(prev) + 1;
    else newOrder = 0;
    persist({ actions: d.actions.map(g => g.id === draggedId ? { ...g, order: newOrder } : g) });
    setDragOverRow(null);
  };

  // Kanban card drop: status follows the column, position follows the card
  const handleCardReorder = (draggedId, targetId, before, colId) => {
    const d = dataRef.current;
    const g = d.actions.find(x => x.id === draggedId);
    if (!g) return;
    if (colId === 'done') { setActionStatus(draggedId, 'done'); return; }
    if ((g.status || 'todo') !== colId) setActionStatus(draggedId, colId);
    reorderAction(draggedId, targetId, before);
  };

  let displayedActions = filteredActions.slice();
  if (focusMode) {
    displayedActions = displayedActions.filter(g => g.pho);
  }

  switch (sortOption) {
    case 'manual':
      displayedActions.sort((a, b) => (a.order ?? a.id) - (b.order ?? b.id));
      break;
    case 'dodate':
      displayedActions.sort((a, b) =>
        (a.doDate || '9999-99-99').localeCompare(b.doDate || '9999-99-99')
        || (a.order ?? a.id) - (b.order ?? b.id));
      break;
    case 'starred':
      displayedActions.sort((a, b) => {
        if (b.pho !== a.pho) return b.pho ? 1 : -1;
        return new Date(b.createdDate || 0) - new Date(a.createdDate || 0);
      });
      break;
    case 'oldest':
      displayedActions.sort((a, b) => new Date(a.createdDate || 0) - new Date(b.createdDate || 0));
      break;
    case 'newest':
      displayedActions.sort((a, b) => new Date(b.createdDate || 0) - new Date(a.createdDate || 0));
      break;
    case 'project':
      displayedActions.sort((a, b) => {
        const projectA = projects.find(p => p.id === a.linkId);
        const projectB = projects.find(p => p.id === b.linkId);

        const goalA = projectA ? goals.find(i => i.id === projectA.goalId) : null;
        const goalB = projectB ? goals.find(i => i.id === projectB.goalId) : null;

        const goalNameA = goalA?.name || '';
        const goalNameB = goalB?.name || '';

        if (goalNameA !== goalNameB) {
          return goalNameA.localeCompare(goalNameB);
        }

        const projectNameA = projectA?.name || '';
        const projectNameB = projectB?.name || '';
        return projectNameA.localeCompare(projectNameB);
      });
      break;
  }

  // Zen navigation sees exactly what the list shows (plain assignment, not a hook)
  zenListRef.current = displayedActions;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 themed-background min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 themed-primary-border mx-auto mb-4"></div>
          <p className="themed-text-secondary">Loading your daily focus tool...</p>
        </div>
      </div>
    );
  }

  const selectedAction = actions.find(g => g.id === selectedActionId)
    || completedActions.find(g => g.id === selectedActionId)
    || null;

  // Narrow windows: the pane slides over the list instead of squeezing beside it
  const paneOverlay = !zenMode && windowWidth < 1000;

  // Compact relative time, e.g. "3m ago", "2d ago".
  const timeAgo = (iso) => {
    if (!iso) return '';
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 45) return 'just now';
    const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24); if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7); if (w < 5) return `${w}w ago`;
    const mo = Math.floor(d / 30); if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
  };

  const addChecklistItem = (actionId, text) => {
    const d = dataRef.current;
    const action = [...d.actions, ...d.completedActions].find(g => g.id === actionId);
    if (!action || !text.trim()) return;
    const newItem = {
      id: Math.max(...(action.checklist?.map(a => a.id) || [0]), 0) + 1,
      text: text.trim(),
      completed: false
    };
    updateAction(actionId, { checklist: [...(action.checklist || []), newItem] });
  };

  // Actions to show in main panel based on sidebar selection
  // Goal solo: one click on a sidebar goal narrows the bench to that goal's projects
  const soloGoalProjectIds = openGoalId !== null
    ? new Set(projects.filter(p => p.goalId === openGoalId).map(p => p.id))
    : null;

  const actionsToShow = openFeatureId === null
    ? (soloGoalProjectIds
        ? displayedActions.filter(g => g.linkType === 'project' && soloGoalProjectIds.has(g.linkId))
        : displayedActions)
    : openFeatureId === 'unassigned'
    ? displayedActions.filter(g => g.linkType !== 'project')
    : displayedActions.filter(g => g.linkType === 'project' && g.linkId === openFeatureId);

  // Completed actions filtered the same way as actionsToShow (for kanban Done column)
  const completedActionsToShow = openFeatureId === null
    ? (soloGoalProjectIds
        ? filteredCompletedActions.filter(g => g.linkType === 'project' && soloGoalProjectIds.has(g.linkId))
        : filteredCompletedActions)
    : openFeatureId === 'unassigned'
    ? filteredCompletedActions.filter(g => g.linkType !== 'project')
    : filteredCompletedActions.filter(g => g.linkType === 'project' && g.linkId === openFeatureId);

  // Build grouped render list: feature headers + action items interleaved
  const renderList = [];
  filteredProjects.filter(p => p.active).forEach(feature => {
    const fg = displayedActions.filter(g => g.linkType === 'project' && g.linkId === feature.id);
    if (!fg.length) return;
    const goal = goals.find(i => i.id === feature.goalId);
    renderList.push({ type: 'feature', feature, count: fg.length, goal });
    if (openFeatureId === feature.id) {
      fg.forEach((action, idx) => renderList.push({ type: 'action', action, idx }));
    }
  });
  const unassignedActions = displayedActions.filter(g => g.linkType !== 'project');
  if (unassignedActions.length) {
    renderList.push({ type: 'unassigned', count: unassignedActions.length });
    if (openFeatureId === 'unassigned') {
      unassignedActions.forEach((action, idx) => renderList.push({ type: 'action', action, idx }));
    }
  }

  // Flat render list for the list view: either grouped (P mode) or plain.
  // A solo is one project — a group banner there would just repeat the name.
  const listRenderItems = (() => {
    // The room list reads in the board's order: what's in motion first,
    // under the board's own tinted lane labels (Paul, Oct 8 2026). When
    // nothing is in motion the sheet stays flat — a lone label is noise.
    // Your drag order still rules within each lane.
    if (roomList) {
      const doing = actionsToShow.filter(a => a.status === 'doing');
      const rest  = actionsToShow.filter(a => a.status !== 'doing');
      if (doing.length === 0) return rest.map(action => ({ type: 'action', action }));
      return [
        { type: 'lane', lane: 'doing', count: doing.length },
        ...doing.map(action => ({ type: 'action', action })),
        { type: 'lane', lane: 'todo', count: rest.length },
        ...rest.map(action => ({ type: 'action', action })),
      ];
    }
    if (!groupByProject || soloProject) return actionsToShow.map(action => ({ type: 'action', action }));
    const map = {};
    const order = [];
    actionsToShow.forEach(g => {
      const key = g.linkType === 'project' && g.linkId ? g.linkId : '__none__';
      if (!map[key]) {
        map[key] = { project: projects.find(p => p.id === key) || null, actions: [] };
        order.push(key);
      }
      map[key].actions.push(g);
    });
    const sorted = order
      .map(k => map[k])
      .sort((a, b) => {
        if (!a.project) return 1;
        if (!b.project) return -1;
        return (a.project.name || '').localeCompare(b.project.name || '');
      });
    return sorted.flatMap(({ project, actions: grpActions }) => [
      { type: 'header', project, count: grpActions.length },
      ...grpActions.map(action => ({ type: 'action', action })),
    ]);
  })();

  // ── The one-line crumb the List and Board headers print ─────────────────
  // The left nav names the goal and the project; this only orients. Counts,
  // charters and mastheads were cut on purpose — a project shows its face in
  // Goals view, nowhere else.
  const wpCrumb = (() => {
    if (openFeatureId && openFeatureId !== 'unassigned') {
      const f = projects.find(p => p.id === openFeatureId);
      const o = f ? goals.find(i => i.id === f.goalId) : null;
      return o ? `${o.name} › ${f?.name}` : (f?.name || 'Project');
    }
    if (openFeatureId === 'unassigned') return 'No project';
    if (openGoalId !== null) return goals.find(i => i.id === openGoalId)?.name || 'All actions';
    return 'All actions';
  })();
  const wpStarred = actions.filter(g => g.pho && !g.completed).length;

  // The theme picker travels: it sits in the unified header everywhere else,
  // and the Goals home renders it in its own top line.
  const themeControl = (
    <div className="relative">
      <button
        onClick={() => setShowThemePicker(p => !p)}
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        title="Change theme"
      >
        <Palette size={15}/>
      </button>
      {showThemePicker && (
        <div
          className="absolute right-0 top-9 themed-background border themed-border rounded-xl shadow-xl p-3 z-50 w-56"
          onMouseLeave={() => setShowThemePicker(false)}
        >
          <p className="text-[10px] uppercase tracking-widest themed-text-muted mb-2.5 px-1">Theme</p>
          <div className="grid grid-cols-7 gap-1.5 mb-2">
            {Object.entries(themes).map(([key, t]) => (
              <button
                key={key}
                onClick={() => { switchTheme(key); setShowThemePicker(false); }}
                title={t.name}
                className="relative w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 focus:outline-none"
                style={{
                  backgroundColor: t.colors.primary,
                  borderColor: currentTheme === key ? t.colors.text : 'transparent',
                  boxShadow: currentTheme === key ? `0 0 0 2px white, 0 0 0 3px ${t.colors.primary}` : 'none',
                }}
              />
            ))}
          </div>
          <p className="text-[11px] themed-text-muted px-1">{themes[currentTheme].name}</p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        /* Custom select dropdown styling */
        select option {
          white-space: normal;
          word-wrap: break-word;
          max-width: 100%;
          padding: 8px;
        }

        /* Pulsing animation for focus mode */
        @keyframes pulse-glow {
          0% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-pho) 40%, transparent); }
          70% { box-shadow: 0 0 0 10px color-mix(in srgb, var(--color-pho) 0%, transparent); }
          100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-pho) 0%, transparent); }
        }

        .animate-pulse-glow {
          animation: pulse-glow 2s infinite;
        }

        /* Done action fly-out animation */
        @keyframes action-done {
          0%   { opacity: 1; transform: translateX(0) scaleY(1); max-height: 60px; }
          40%  { opacity: 0.6; transform: translateX(12px) scaleY(0.8); }
          100% { opacity: 0; transform: translateX(20px) scaleY(0); max-height: 0; padding: 0; margin: 0; }
        }
        .action-completing {
          animation: action-done 0.55s ease-in forwards;
          pointer-events: none;
          overflow: hidden;
        }
      `}</style>
      {/* Working Papers — the day book's own stylesheet (see workingPapers.js) */}
      <style>{WP_LIST_CSS}</style>
      
      {/* ── 409 conflict banner ── */}
      {saveStatus === 'conflict' && (
        <div className="fixed top-0 inset-x-0 z-[100] flex items-center justify-between gap-4 px-5 py-3 text-sm text-white shadow-lg" style={{ background: '#f97316' }}>
          <div className="flex items-center gap-2.5">
            <AlertTriangle size={16} className="flex-shrink-0" />
            <span>Another session saved newer data. Your last change wasn't applied.</span>
          </div>
          <button
            onClick={() => { loadData(); setSaveStatus('idle'); }}
            className="flex-shrink-0 px-3 py-1 text-xs font-semibold rounded bg-white text-orange-600 hover:bg-orange-50 transition-colors"
          >
            Reload latest
          </button>
        </div>
      )}

      {/* ── The solo room's / the wall's masthead ─────────────────────── */}
      {(minimalSolo || wallMode) && (
        <div className="flex items-center gap-4 px-6 themed-background" style={{ height: 52 }}>
          {!wallMode && (
            <button onClick={closeSoloToHome}
              className="px-2.5 py-1 text-[12px] rounded-md border themed-border themed-surface themed-text-muted hover:themed-text"
              title="Back to the wall (Esc)">‹ Home</button>
          )}
          <span className="text-[15px] font-bold themed-text select-none"
            style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace", letterSpacing: '0.03em' }}>
            phokii3
          </span>
          {/* The wall's lens as CHIPS (Paul, Oct 9): on a control surface the
              lens is the thing you came to press — it earns its border. */}
          {wallMode && (
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[9.5px] font-bold uppercase themed-text-muted select-none flex-shrink-0 mr-1"
                style={{ letterSpacing: '0.14em', fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace" }}>
                Lens
              </span>
              {[{ id: null, name: 'Everything' },
                ...[...goals.filter(g => g.active)].sort((a, b) => a.name.localeCompare(b.name))].map(g => (
                <button key={g.id == null ? 'all' : g.id} onClick={() => setWallLens(g.id)}
                  className="text-[12px] px-3 py-1 rounded-full border cursor-pointer truncate flex-shrink-0"
                  style={wallLens === g.id
                    ? { borderColor: 'var(--color-primary)', color: 'var(--color-text)', background: 'var(--color-surface)', fontWeight: 600 }
                    : { borderColor: 'var(--color-border)', color: 'var(--color-textMuted)', background: 'transparent' }}
                  onMouseEnter={(e) => { if (wallLens !== g.id) e.currentTarget.style.color = 'var(--color-text)'; }}
                  onMouseLeave={(e) => { if (wallLens !== g.id) e.currentTarget.style.color = 'var(--color-textMuted)'; }}>
                  {g.name}
                </button>
              ))}
              <button onClick={() => setGoalsRoomOpen(true)}
                className="text-[12px] themed-text-muted hover:themed-text bg-transparent border-0 cursor-pointer flex-shrink-0"
                title="The planning room — plan goals, see goals, load premade ones (G)">
                manage goals
              </button>
            </div>
          )}
          {/* The chips — where you are, as doors. Goal re-aims, project jumps. */}
          {!wallMode && (() => {
            const curProj = projects.find(p => p.id === openFeatureId);
            const curGoal = curProj && curProj.goalId != null ? goals.find(g => g.id === curProj.goalId) : null;
            const activeP = projects.filter(p => p.active);
            // Written as a sentence, not a cockpit: plain words with a small
            // caret, no borders or fills — four capsules up here raised
            // Paul's pulse (Oct 8 2026). The menus beneath are unchanged.
            return (
              <div ref={roomPickerRef} className="relative flex items-center gap-3 min-w-0">
                <button onClick={() => setRoomPicker(rp => rp ? null : { stage: 'goals' })}
                  className="flex items-baseline gap-1 min-w-0 bg-transparent border-0 cursor-pointer text-[13px] themed-text-muted hover:themed-text"
                  style={{ maxWidth: 210 }} title="Change goal">
                  <span className="truncate">{curGoal ? curGoal.name : 'No goal'}</span>
                  <span className="text-[9px] flex-shrink-0">▾</span>
                </button>
                <button onClick={() => setRoomPicker(rp => rp ? null : { stage: 'projects', goalId: curProj ? (curProj.goalId ?? null) : null })}
                  className="flex items-baseline gap-1 min-w-0 bg-transparent border-0 cursor-pointer text-[14px] font-semibold themed-text hover:underline"
                  style={{ maxWidth: 280 }} title="Change project — or open this project's page">
                  <span className="truncate">{curProj ? curProj.name : ''}</span>
                  <span className="text-[9px] themed-text-muted flex-shrink-0">▾</span>
                </button>
                {roomPicker && (
                  <div role="menu" className="absolute left-0 themed-background border themed-border rounded-lg shadow-lg py-1 z-40 min-w-[240px]"
                    style={{ top: 'calc(100% + 6px)' }}>
                    {roomPicker.stage === 'goals' && (<>
                      {[...goals.filter(g => g.active)].sort((a, b) => a.name.localeCompare(b.name)).map(g => (
                        <button key={g.id} onClick={() => setRoomPicker({ stage: 'projects', goalId: g.id })}
                          className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text hover:themed-surface-hover truncate">
                          {g.name} <span className="themed-text-muted">›</span>
                        </button>
                      ))}
                      {activeP.some(p => p.goalId == null) && (
                        <button onClick={() => setRoomPicker({ stage: 'projects', goalId: null })}
                          className="w-full text-left px-3 py-1.5 text-[12.5px] themed-text-muted hover:themed-text truncate">
                          No goal ›
                        </button>
                      )}
                    </>)}
                    {roomPicker.stage === 'projects' && (() => {
                      const rows = activeP
                        .filter(p => roomPicker.goalId == null ? p.goalId == null : p.goalId === roomPicker.goalId)
                        .sort((a, b) => a.name.localeCompare(b.name));
                      const samePlace = curProj && ((curProj.goalId ?? null) === roomPicker.goalId);
                      return (<>
                        {samePlace && (
                          <button onClick={() => { setRoomPicker(null); openLedgerAt('project', openFeatureId); }}
                            className="w-full text-left px-3 py-1.5 text-[12px] themed-primary hover:themed-surface-hover">
                            Open this project's page →
                          </button>
                        )}
                        {rows.map(p => (
                          <button key={p.id}
                            onClick={() => { setRoomPicker(null); if (p.id !== openFeatureId) openProjectSolo(p.id); }}
                            className={`w-full text-left px-3 py-1.5 text-[12.5px] hover:themed-surface-hover truncate themed-text ${p.id === openFeatureId ? 'font-semibold' : ''}`}>
                            <span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: p.color || 'var(--color-border)' }} />
                            {p.name}
                          </button>
                        ))}
                        {rows.length === 0 && <p className="px-3 py-2 text-[12px] themed-text-muted">No projects here yet.</p>}
                        {/* a project is born where you notice it's missing:
                            name it, Enter, and you're standing in its room */}
                        <div className="border-t themed-border-light mt-1 pt-1.5 px-2 pb-1.5">
                          <input
                            type="text"
                            placeholder={`+ new project in ${roomPicker.goalId == null ? 'No goal' : (goals.find(g => g.id === roomPicker.goalId)?.name || '')}`}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter') return;
                              const name = e.target.value.trim();
                              if (!name) return;
                              const id = dataRef.current.nextProjectId;
                              addProjectInline(name, roomPicker.goalId);
                              setRoomPicker(null);
                              openProjectSolo(id);
                            }}
                            className="w-full text-[12.5px] themed-text bg-transparent border themed-border rounded-md px-2 py-1.5 focus:outline-none placeholder:themed-text-muted"
                          />
                        </div>
                      </>);
                    })()}
                  </div>
                )}
              </div>
            );
          })()}
          <span className="flex-1" />
          {/* ✦ — F made visible as one glyph, never a written instruction.
              Faint until you star something; filled while focus is on. */}
          <button onClick={() => setFocusMode(m => !m)}
            className="flex-shrink-0 bg-transparent border-0 cursor-pointer text-[15px] leading-none transition-opacity"
            style={focusMode
              ? { color: 'var(--color-background)', background: 'var(--color-pho)', borderRadius: 999, padding: '4px 8px' }
              : { color: 'var(--color-pho)', opacity: wpStarred > 0 ? 1 : 0.35, padding: '4px 4px' }}
            title={focusMode ? 'Show everything again (F)'
              : wpStarred > 0 ? 'Only what you starred (F)'
              : 'Star a task (✦) and this narrows the room to it (F)'}>
            ✦
          </button>
          {/* the launcher — a visible field on the wall, a bare keycap in the room */}
          {wallMode ? (
            <button onClick={() => setSearchOpen(true)}
              className="flex items-center gap-2 px-3.5 py-1.5 text-[12.5px] rounded-full border themed-border themed-surface themed-text-muted hover:themed-text flex-shrink-0"
              title="Search everything (⌘K)">
              Jump to anything…
              <kbd className="text-[10px] border themed-border rounded px-1 py-0.5"
                style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>⌘K</kbd>
            </button>
          ) : (
            <button onClick={() => setSearchOpen(true)}
              className="flex-shrink-0 bg-transparent border-0 cursor-pointer"
              title="Search everything (⌘K)">
              <kbd className="text-[10px] border themed-border rounded px-1.5 py-0.5 themed-text-muted"
                style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>⌘K</kbd>
            </button>
          )}
          {wallMode
            ? (<>
              {themeControl}
              <button onClick={onLogout} className="text-xs themed-text-muted hover:themed-text flex-shrink-0">log out</button>
            </>)
            : (<>
              <button onClick={() => setKanbanMode(false)}
                className={`text-[11px] ${!kanbanMode ? 'themed-text font-semibold' : 'themed-text-muted hover:themed-text'}`}
                title="List (L)">list</button>
              <button onClick={() => setKanbanMode(true)}
                className={`text-[11px] ${kanbanMode ? 'themed-text font-semibold' : 'themed-text-muted hover:themed-text'}`}
                title="Board (B)">board</button>
            </>)}
        </div>
      )}

      {/* ── Unified app header — everywhere except the Goals home and the
             solo room, which draw their own top lines ───────────────────── */}
      {!(ledgerView && ledgerAtHome && !zenMode) && !minimalSolo && !wallMode && (
      <div className="flex items-center px-4 py-2 border-b themed-border themed-background" style={{height: '44px'}}>
        {/* Sidebar toggle */}
        <button
          onClick={() => setSidebarCollapsed(c => !c)}
          className="p-1.5 mr-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed
            ? <ChevronRight size={15}/>
            : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
          }
        </button>
        {/* The comp's wordmark replaced the logo image everywhere (Oct 7 2026) */}
        <span className="text-[14px] font-bold themed-text select-none mr-4"
          style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace", letterSpacing: '0.03em' }}>
          phokii3
        </span>
        <div className="flex items-center gap-1 flex-1">
          {/* Segmented view switcher (focus lives in the ✦ pill in the list header) */}
          <div className="flex items-center border themed-border rounded-md overflow-hidden ml-2" style={{height:'28px'}}>
            {/* Goals leads: it is the main flow, and it is the default.
                Then the three arrangements of the same work — List, Board,
                Calendar. Every segment but the last carries border-r. */}
            <button
              onClick={() => { setZenMode(false); setLedgerView(true); setKanbanMode(false); setMilestoneView(false); setCalendarView(false); }}
              className={`px-2.5 h-full flex items-center gap-1.5 text-xs transition-colors border-r themed-border ${ledgerView ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="Goals (G or 1) — where the work lives"
            >
              <BookOpen size={12}/>
              Goals
            </button>
            <button
              onClick={() => { setZenMode(false); setKanbanMode(false); setMilestoneView(false); setCalendarView(false); setLedgerView(false); }}
              className={`px-2.5 h-full flex items-center gap-1.5 text-xs transition-colors border-r themed-border ${!kanbanMode && !milestoneView && !calendarView && !ledgerView ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="List (L or 2)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
              List
            </button>
            <button
              onClick={() => { setZenMode(false); setKanbanMode(true); setMilestoneView(false); setCalendarView(false); setLedgerView(false); }}
              className={`px-2.5 h-full flex items-center gap-1.5 text-xs transition-colors border-r themed-border ${kanbanMode && !milestoneView && !calendarView && !ledgerView ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="Board (B or 3)"
            >
              <Columns2 size={12}/>
              Board
            </button>
            <button
              onClick={() => { setZenMode(false); setCalendarView(true); setKanbanMode(false); setMilestoneView(false); setLedgerView(false); }}
              className={`px-2.5 h-full flex items-center gap-1.5 text-xs transition-colors ${calendarView && !milestoneView && !ledgerView ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-50'}`}
              title="Calendar (C or 4) — Timeline still lives on M"
            >
              <Calendar size={12}/>
              Calendar
            </button>
          </div>
        </div>
        {/* Search */}
        <button
          onClick={() => setSearchOpen(true)}
          className="p-1.5 mr-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Search everything (⌘K)"
        >
          <Search size={15}/>
        </button>
        {/* Theme picker button */}
        <div className="mr-3">{themeControl}</div>
        <span
          className="text-xs mr-3 transition-all duration-300"
          aria-live="polite"
          style={{
            opacity: saveStatus === 'idle' ? 0 : 1,
            color: saveStatus === 'saved' ? '#16a34a'
                 : saveStatus === 'error'  ? '#dc2626'
                 : 'var(--color-textMuted)',
          }}
        >
          {saveStatus === 'saving' && 'Saving…'}
          {saveStatus === 'saved'  && 'Saved ✓'}
          {saveStatus === 'error'  && 'Save failed'}
        </span>
        <button onClick={onLogout} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">logout</button>
      </div>
      )}

      <div className={`daily-focus-tool max-w-full mx-auto themed-background transition-all duration-300${zenReturning ? ' zen-return' : ''}`} style={{ fontFamily: 'var(--font-main)' }}>
      {/* Daily Brief */}
      {goalsRoomOpen && (
        <GoalsRoom
          goals={goals} projects={projects}
          updateGoal={updateGoal} updateProject={updateProject}
          addGoalInline={addGoalInline} addProjectInline={addProjectInline}
          openLibrary={() => setPlantingOpen('library')}
          openProjectSolo={openProjectSolo}
          onClose={() => setGoalsRoomOpen(false)}
        />
      )}
      {plantingOpen && (
        <Planting
          initialStage={plantingOpen === 'library' ? 'library' : 'goal'}
          onClose={() => setPlantingOpen(false)}
          onPlantBare={plantGoalBare}
          onPlantBundles={plantBundles}
          onSaveIntention={savePlantingIntention}
          onOpenGoal={(id) => openLedgerAt('goal', id)}
        />
      )}

      <DailyBrief
        open={briefOpen}
        onClose={() => setBriefOpen(false)}
        actions={actions}
        completedActions={completedActions}
        deferredActions={deferredActions}
        projects={projects}
        goals={goals}
        onOpenAction={(id) => { setBriefOpen(false); setSelectedActionId(id); }}
        onOpenDeferred={() => { setBriefOpen(false); setShowDeferredReview(true); }}
        updateAction={updateAction}
      />
      {/* ⌘K search palette */}
      <SearchPalette
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        actions={actions}
        completedActions={completedActions}
        archivedActions={archivedActions}
        projects={projects}
        goals={goals}
        onOpenAction={(id) => { setSelectedActionId(id); setSearchOpen(false); }}
      />
      {/* ── Keyboard shortcuts ──────────────────────────────────────────
          A centred modal over a dim backdrop, the same overlay idiom as
          Recently deleted and the create modal. Grouped by the surface each
          key actually works on, so the panel answers "what can I do *here*"
          rather than listing every key in the app in one column. Rows are
          left-justified against .App's text-align:center (the fifth time
          that inherited centring has bitten — see the build notes). */}
      <button
        onClick={() => setShowKeyboardShortcuts(true)}
        className="fixed bottom-4 left-4 z-50 text-[11px] themed-text-muted themed-background px-2.5 py-1.5 rounded-lg shadow-lg border themed-border hover:themed-text transition-colors"
        title="Keyboard shortcuts (S)"
      >
        Keyboard shortcuts <span className="opacity-60">(S)</span>
      </button>
      {showKeyboardShortcuts && (
        <div
          className="fixed inset-0 z-[96] flex items-center justify-center p-6"
          style={{ background: 'rgba(0,0,0,0.28)' }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setShowKeyboardShortcuts(false); }}
        >
          <div className="w-[660px] max-w-[94vw] max-h-[86vh] overflow-y-auto rounded-xl shadow-2xl border themed-border themed-background" style={{ textAlign: 'left' }}>
            <div className="px-6 py-3.5 border-b themed-border-light flex items-center justify-between sticky top-0 themed-background z-10">
              <h3 className="text-sm font-bold themed-text">Keyboard shortcuts</h3>
              <button onClick={() => setShowKeyboardShortcuts(false)} className="themed-text-muted hover:themed-text" title="Close (Esc or S)"><X size={15}/></button>
            </div>
            <div className="px-6 py-5">
              {[
                { surface: 'Goals', rows: [
                  ['G  or  1', 'The planning room — plan goals, see goals, load premade'],
                  ['Space', "Open the book you're on — or, from inside a goal, back to all of them"],
                  ['N', 'A new action in the open project — or a new project in the open goal'],
                ] },
                { surface: 'The work', rows: [
                  ['H  or  ⇧B', 'Home — the wall: every project as a strip, active work on top'],
                  ['S  on a strip', 'Solo it in place — ⇧-click to stack solos'],
                  ['L', 'The room as a list — from the wall, your last room'],
                  ['B', 'The room as a board — from the wall, your last room'],
                  ['N', 'A new action — opens a fresh line (list)'],
                ] },
                { surface: 'The old world (kept for now)', rows: [
                  ['⇧L  or  2 / 3', 'The old List and Board — sidebar and all'],
                  ['A', 'Drop the filter — every action again'],
                  ['P', 'Group by project, on or off'],
                  ['0', 'Hide the projects with nothing in them'],
                ] },
                { surface: 'Calendar', rows: [
                  ['C  or  4', 'Open the Calendar'],
                  ['D', "Flip the lens — your Do days, or the Due days the world set"],
                ] },
                { surface: 'Anywhere', rows: [
                  ['⌘K', 'Search everything — titles, notes, how things ended'],
                  ['F', 'Focus — only what you starred'],
                  ['U', 'Take every star off'],
                  ['↑ ↓', "In the full-screen note: at the top or bottom edge, flow to the previous or next action — or swipe sideways"],
                  ['⌘⇧[  ⌘⇧]', 'Previous / next action while writing'],
                  // M (milestone timeline) hidden — dev stopped, surface may be
                  // shelved; the key still works but is not taught (UX-17).
                  ['T', 'Cycle themes — , and . step back and forward'],
                  ['S', 'This panel'],
                  ['Esc', 'Close, cancel, or step back one level'],
                ] },
              ].map(({ surface, rows }) => (
                <div key={surface} className="mb-5 last:mb-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] themed-text-muted pb-1.5 mb-2 border-b themed-border-light">
                    {surface}
                  </div>
                  <table className="w-full text-xs border-collapse" style={{ textAlign: 'left' }}>
                    <tbody>
                      {rows.map(([key, what]) => (
                        <tr key={`${surface}-${key}`}>
                          <td className="pr-4 py-1 align-top whitespace-nowrap w-[92px] text-left font-semibold themed-text">{key}</td>
                          <td className="py-1 align-top text-left themed-text leading-relaxed">{what}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
        
<div className="flex" style={(minimalSolo || wallMode)
  // The room (and the wall) is one viewport: each region scrolls on its
  // own, never the page (Paul, Oct 7 2026).
  ? { height: 'calc(100vh - 52px)', overflow: 'hidden' }
  : { minHeight: 'calc(100vh - 56px)' }}>

          {/* Undo toast — every delete gets six seconds of grace */}
          {undoToast && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[95] flex items-center gap-3 px-4 py-2.5 rounded-full text-xs font-medium shadow-lg" style={{ background: 'var(--color-text)', color: 'var(--color-background)' }}>
              <span>{undoToast.message}{undoToast.plain || undoToast.message.startsWith('Planted') ? '' : ' — in Recently deleted for 30 days'}</span>
              <button onClick={undoToast.restore} className="font-bold underline underline-offset-2">{undoToast.actionLabel || 'Undo'}</button>
            </div>
          )}

          {/* U — take every star off, but ask first (UX-6). In-app, never a
              browser confirm; acting also offers the undo toast. */}
          {confirmUnstarAll && (
            <div className="fixed inset-0 z-[95] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.28)' }} onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmUnstarAll(false); }}>
              <div className="w-[380px] max-w-[90vw] rounded-xl shadow-2xl border themed-border themed-background p-5">
                <h3 className="text-sm font-bold themed-text mb-1.5">Take every star off?</h3>
                <p className="text-[12.5px] themed-text-muted leading-relaxed mb-4">
                  {confirmUnstarAll.stars === 1 ? '1 star comes' : `${confirmUnstarAll.stars} stars across ${confirmUnstarAll.goals === 1 ? '1 goal' : `${confirmUnstarAll.goals} goals`} come`} off. Nothing is deleted — the marks just lift.
                </p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setConfirmUnstarAll(false)} className="px-3 py-1.5 text-xs font-medium themed-text-muted hover:themed-text rounded-lg border themed-border">Keep them</button>
                  <button
                    onClick={() => {
                      const d = dataRef.current;
                      const prev = {
                        actions: d.actions, completedActions: d.completedActions,
                        projects: d.projects, goals: d.goals,
                      };
                      persist({
                        actions: d.actions.map(g => ({ ...g, pho: false })),
                        completedActions: d.completedActions.map(g => ({ ...g, pho: false })),
                        projects: d.projects.map(p => ({ ...p, pho: false })),
                        goals: d.goals.map(i => ({ ...i, pho: false })),
                      });
                      setConfirmUnstarAll(false);
                      setUndoToast({
                        message: 'Every star taken off',
                        plain: true,
                        actionLabel: 'Put them back',
                        restore: () => { persist(prev); setUndoToast(null); },
                      });
                      setTimeout(() => setUndoToast(cur => (cur && cur.message === 'Every star taken off' ? null : cur)), 6000);
                    }}
                    className="px-3 py-1.5 text-xs font-semibold rounded-lg"
                    style={{ background: 'var(--color-pho)', color: 'var(--color-background)' }}
                  >
                    Take them off
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Recently deleted */}
          {showTrashModal && (
            <div className="fixed inset-0 z-[90] flex items-start justify-center" style={{ background: 'rgba(0,0,0,0.28)', paddingTop: '12vh' }} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowTrashModal(false); }}>
              <div className="w-[560px] max-w-[92vw] rounded-xl shadow-2xl border themed-border themed-background overflow-hidden">
                <div className="px-5 py-3.5 border-b themed-border-light flex items-center justify-between">
                  <h3 className="text-sm font-bold themed-text">Recently deleted</h3>
                  <button onClick={() => setShowTrashModal(false)} className="themed-text-muted hover:themed-text"><X size={15}/></button>
                </div>
                <div className="max-h-[50vh] overflow-y-auto py-1">
                  {trash.length === 0 && <p className="px-5 py-8 text-sm themed-text-muted text-center">Nothing here — deletes rest for 30 days, then leave for good.</p>}
                  {trash.map((t, i) => (
                    <div key={i} className="px-5 py-2.5 flex items-center gap-3 border-b themed-border-light last:border-b-0">
                      <span className="text-[9px] font-bold uppercase tracking-wider themed-text-muted w-14 flex-shrink-0">{t.kind === 'goal' ? 'goal' : t.kind}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[13px] themed-text truncate">{t.object?.text || t.object?.name}</p>
                        <p className="text-[10px] themed-text-muted">
                          deleted {new Date(t.deletedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                          {t.children && ` · with ${(t.children.projects?.length || 0) ? `${t.children.projects.length} projects, ` : ''}${(t.children.actions?.length || 0) + (t.children.completedActions?.length || 0)} actions`}
                        </p>
                      </div>
                      <button onClick={() => restoreFromTrash(i)} className="text-[11px] font-semibold themed-primary hover:underline flex-shrink-0">Restore</button>
                      <button onClick={() => deleteForever(i)} className="text-[11px] font-semibold text-red-500 hover:underline flex-shrink-0">Delete forever</button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* H-with-nothing-selected hint */}
          {zenHint && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full text-xs font-medium shadow-lg" style={{ background: 'var(--color-text)', color: 'var(--color-background)' }}>
              Select an action first — then H opens it full screen
            </div>
          )}

          {/* Sidebar — hidden entirely in zen writing mode AND in calendar
              (the calendar owns the whole screen, like zen and the Brief) */}
          <Sidebar
            zenMode={zenMode || calendarView || ledgerView || minimalSolo || wallMode}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
            goals={goals}
            projects={projects}
            actions={actions}
            openGoalId={openGoalId}
            setOpenGoalId={setOpenGoalId}
            openFeatureId={openFeatureId}
            setOpenFeatureId={(v) => { setSoloFromHome(false); setSuperView(false); setOpenFeatureId(v); }}
            expandedGoals={expandedGoals}
            filteredGoals={filteredGoals}
            filteredProjects={filteredProjects}
            getActionCountForProject={getActionCountForProject}
            updateGoal={updateGoal}
            updateProject={updateProject}
            deleteGoal={deleteGoal}
            deleteProject={deleteProject}
            addingProjectForGoal={addingProjectForGoal}
            setAddingProjectForGoal={setAddingProjectForGoal}
            inlineProjectName={inlineProjectName}
            setInlineProjectName={setInlineProjectName}
            addingGoal={addingGoal}
            setAddingGoal={setAddingGoal}
            inlineGoalName={inlineGoalName}
            setInlineGoalName={setInlineGoalName}
            unlinkedActions={unlinkedActions}
            setShowBatchAssignModal={setShowBatchAssignModal}
            setShowProjectImportModal={setShowProjectImportModal}
            setProjectImportJson={setProjectImportJson}
            setProjectImportError={setProjectImportError}
            setProjectImportResult={setProjectImportResult}
            copyAiPrompt={copyAiPrompt}
            aiPromptCopied={aiPromptCopied}
            exportBackup={exportBackup}
            loadStarterProject={loadStarterProject}
            trashCount={trash.length}
            openTrash={() => setShowTrashModal(true)}
            openLedgerAt={openLedgerAt}
            setShowExportModal={setShowExportModal}
            getAuthHeadersOnly={getAuthHeadersOnly}
            loadData={loadData}
            managementMode={managementMode}
            setManagementMode={setManagementMode}
            setEditingProjectName={setEditingProjectName}
            editingProjectName={editingProjectName}
            setEditingGoalName={setEditingGoalName}
            editingGoalName={editingGoalName}
            setShowCreateModal={setShowCreateModal}
            setCreateModalTab={setCreateModalTab}
            projectPhoFlash={projectPhoFlash}
            goalPhoFlash={goalPhoFlash}
            toggleGoalExpanded={toggleGoalExpanded}
            addProjectInline={addProjectInline}
            addGoalInline={addGoalInline}
            displayedActions={displayedActions}
            setSelectedProjectId={setSelectedProjectId}
            setNewProjectGoal={setNewProjectGoal}
          />

          {/* ══ THE WALL — home. Every project a fixed 280px mixer strip
                 (the DAW law), in motion on top, side-scroll for the rest.
                 S solos in place, ⇧S stacks solos. Replaced the deck and
                 the question-home (parked behind G), Oct 9 2026. ══ */}
          {wallMode && (() => {
            const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";
            const LANE = { doing: 'var(--color-warning)', todo: 'var(--color-primary)', done: 'var(--color-success)' };
            const tracks = projects.filter(p => p.active)
              .filter(p => wallLens == null || p.goalId === wallLens)
              .sort(wallCompare);
            const goalName = (p) => goals.find(g => g.id === p.goalId)?.name || '';
            const soloing = wallSolo.size > 0;
            const toggleSolo = (id, shift) => setWallSolo(prev => {
              const next = new Set(prev);
              if (shift) { next.has(id) ? next.delete(id) : next.add(id); return next; }
              if (prev.size === 1 && prev.has(id)) return new Set();
              return new Set([id]);
            });
            const hintFor = (a) => {
              const today = new Date(); today.setHours(0, 0, 0, 0);
              if (a.dueDate && new Date(a.dueDate) < today)
                return { text: `Overdue ${new Date(a.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`, tone: 'var(--color-error)' };
              const left = (a.checklist || []).filter(c => !c.completed).length;
              if (left > 0) return { text: `${left} step${left === 1 ? '' : 's'} left on the checklist`, tone: 'var(--color-textMuted)' };
              return null;
            };
            const cell = (a, lane, extra) => {
              const hint = lane === 'done' ? null : hintFor(a);
              return (
                <button key={`${lane}-${a.id}`}
                  onClick={() => a.id != null && setSelectedActionId(a.id === selectedActionId ? null : a.id)}
                  className="w-full text-left rounded-md themed-surface hover:themed-surface-hover"
                  style={{
                    border: `1px solid ${a.id === selectedActionId ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderLeft: `3px solid ${LANE[lane]}`,
                    padding: '8px 9px', fontSize: 12, lineHeight: 1.35,
                  }}>
                  <span style={lane === 'done'
                    ? { color: 'var(--color-textMuted)', textDecoration: 'line-through', textDecorationColor: 'color-mix(in srgb, var(--color-success) 50%, transparent)' }
                    : { color: 'var(--color-text)' }}>
                    {a.pho && <span style={{ color: 'var(--color-pho)', marginRight: 5 }}>✦</span>}{a.text}
                  </span>
                  {hint && <span className="block mt-1 text-[10.5px] truncate" style={{ color: hint.tone }}>{hint.text}</span>}
                  {extra && <span className="block mt-1 text-[10.5px]" style={{ color: 'var(--color-success)' }}>↳ {extra}</span>}
                </button>
              );
            };
            return (
              <div className="flex-1 min-w-0 overflow-x-auto overflow-y-auto themed-background"
                style={{
                  backgroundImage: 'linear-gradient(var(--color-gridLine) 1px, transparent 1px), linear-gradient(90deg, var(--color-gridLine) 1px, transparent 1px)',
                  backgroundSize: '28px 28px',
                  scrollPaddingRight: 24,
                  // the bottom editor docks over the floor — keep the last rows reachable
                  paddingBottom: selectedActionId && paneDock === 'bottom' ? 400 : 0,
                }}>
                <div className="flex items-start gap-3 px-5 pt-4 pb-16">
                  {tracks.map(pr => {
                    const mine = actions.filter(a => a.linkType === 'project' && a.linkId === pr.id && !a.completed)
                      .filter(a => !focusMode || a.pho);
                    const doing = mine.filter(a => a.status === 'doing');
                    const todo = mine.filter(a => a.status !== 'doing' && a.status !== 'done');
                    const doneNow = mine.filter(a => a.status === 'done');
                    // ALL of today's closes, not "the last 2" (a mock-demo cap
                    // that ate Paul's finished work, Oct 9 2026). Yesterday's
                    // receipts live in the room, not on the wall.
                    const dayStart = new Date(); dayStart.setHours(0, 0, 0, 0);
                    const doneRecent = completedActions.filter(a => a.linkId === pr.id
                        && a.completedDate && new Date(a.completedDate) >= dayStart)
                      .sort((a, b) => new Date(b.completedDate || 0) - new Date(a.completedDate || 0));
                    const dimmed = soloing && !wallSolo.has(pr.id);
                    const selAct = selectedActionId != null ? actions.find(a => a.id === selectedActionId) : null;
                    const recede = !dimmed && selAct && selAct.linkId !== pr.id;
                    const lanes = [
                      ['doing', 'active', doing, null],
                      ['todo', 'open', todo, null],
                    ];
                    return (
                      <div key={pr.id} className="flex-shrink-0 flex flex-col gap-2"
                        onDragOver={(e) => {
                          if (!e.dataTransfer.types.includes('wallstrip')) return;
                          e.preventDefault();
                          const r = e.currentTarget.getBoundingClientRect();
                          const before = e.clientX < r.left + r.width / 2;
                          setWallDragOver(prev => (prev && prev.id === pr.id && prev.before === before) ? prev : { id: pr.id, before });
                        }}
                        onDragLeave={() => setWallDragOver(prev => (prev && prev.id === pr.id) ? null : prev)}
                        onDrop={(e) => {
                          const draggedId = parseInt(e.dataTransfer.getData('wallstrip'));
                          if (!draggedId) return;
                          e.preventDefault();
                          const r = e.currentTarget.getBoundingClientRect();
                          reorderWallStrips(draggedId, pr.id, e.clientX < r.left + r.width / 2);
                          setWallDragOver(null);
                        }}
                        style={{
                          width: 280,
                          opacity: dimmed ? 0.18 : recede ? 0.62 : 1,
                          pointerEvents: dimmed ? 'none' : 'auto',
                          transition: 'opacity .15s',
                          boxShadow: wallDragOver && wallDragOver.id === pr.id
                            ? (wallDragOver.before ? '-3px 0 0 0 var(--color-primary)' : '3px 0 0 0 var(--color-primary)')
                            : 'none',
                        }}>
                        <div className="flex items-start gap-2 pb-2"
                          draggable
                          onDragStart={(e) => { e.dataTransfer.setData('wallstrip', String(pr.id)); e.dataTransfer.effectAllowed = 'move'; }}
                          onDragEnd={() => setWallDragOver(null)}
                          title="Drag to reorder — left is priority"
                          style={{ borderBottom: '2px solid var(--color-border)', cursor: 'grab' }}>
                          <div className="flex-1 min-w-0">
                            <button onClick={() => openProjectSolo(pr.id)}
                              className="block text-left text-[13.5px] font-bold themed-text hover:underline leading-tight bg-transparent border-0 cursor-pointer p-0"
                              title="Open the room — this project, full size">
                              {pr.name}
                            </button>
                            <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--color-faint)' }}>{goalName(pr)}</p>
                          </div>
                          <button
                            onClick={(e) => toggleSolo(pr.id, e.shiftKey)}
                            className="flex-shrink-0 w-6 h-6 rounded-md border text-[11px] font-bold cursor-pointer"
                            style={wallSolo.has(pr.id)
                              ? { background: 'var(--color-primary)', color: 'var(--color-background)', borderColor: 'var(--color-primary)' }
                              : { background: 'transparent', color: 'var(--color-textMuted)', borderColor: 'var(--color-border)' }}
                            title="Solo this strip — ⇧-click to stack solos">
                            S
                          </button>
                        </div>
                        {lanes.map(([key, label, items]) => items.length > 0 && (
                          <React.Fragment key={key}>
                            <div className="flex items-baseline gap-1.5 px-0.5 pt-1">
                              <span className="text-[10.5px] font-semibold select-none"
                                style={{ fontFamily: MONO, color: LANE[key] }}>{label}</span>
                              <span className="text-[10.5px] select-none" style={{ fontFamily: MONO, color: 'var(--color-faint)' }}>{items.length}</span>
                            </div>
                            {items.map(a => cell(a, key))}
                          </React.Fragment>
                        ))}
                        {(doneNow.length > 0 || doneRecent.length > 0) && (
                          <React.Fragment>
                            <div className="flex items-baseline gap-1.5 px-0.5 pt-1">
                              <span className="text-[10.5px] font-semibold select-none"
                                style={{ fontFamily: MONO, color: LANE.done }}>done</span>
                              <span className="text-[10.5px] select-none" style={{ fontFamily: MONO, color: 'var(--color-faint)' }}>{doneNow.length + doneRecent.length}</span>
                            </div>
                            {doneNow.map(a => cell(a, 'done'))}
                            {doneRecent.map((a, i) => cell({ ...a, id: a.id }, 'done', a.resolutionComment))}
                          </React.Fragment>
                        )}
                        {mine.length === 0 && doneRecent.length === 0 && (
                          <p className="text-[11px] themed-text-muted text-center py-4">nothing open</p>
                        )}
                      </div>
                    );
                  })}
                  {tracks.length === 0 && (
                    <p className="text-[13px] themed-text-muted py-10 px-2">Nothing under this lens.</p>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Milestone Timeline — replaces list+detail when active */}
          {milestoneView && (
            <div className="flex-1 overflow-hidden" style={{ minHeight: 'calc(100vh - 56px)' }}>
              <MilestoneTimeline
                milestones={milestones}
                projects={projects.filter(p => p.active !== false)}
                goals={goals.filter(i => i.active !== false)}
                onUpdateMilestone={updateMilestone}
                onCreateMilestone={createMilestone}
                onDeleteMilestone={deleteMilestone}
              />
            </div>
          )}

          {/* Main Column + Resize handle + Detail — hidden in milestone view */}
          {/* Calendar — full-bleed like the timeline; hidden while zen shows the note */}
          {ledgerView && !zenMode && (
            <Ledger
              holdSettle={!!briefOpen}
              goals={goals}
              projects={projects}
              actions={actions}
              completedActions={completedActions}
              trash={trash}
              updateGoal={updateGoal}
              updateProject={updateProject}
              deleteGoal={deleteGoal}
              deleteProject={deleteProject}
              restoreFromTrash={restoreFromTrash}
              deleteForever={deleteForever}
              jump={ledgerJump}
              posRef={ledgerPosRef}
              onOpenAction={(id) => { setLedgerView(false); setSelectedActionId(id); }}
              onExit={() => setLedgerView(false)}
              openProjectSolo={openProjectSolo}
              onHomeChange={setLedgerAtHome}
              onLogout={onLogout}
              themeControl={themeControl}
              openPlanting={() => setPlantingOpen(true)}
              openLibrary={() => setPlantingOpen('library')}
              // Clicking into a note IS the intent to write — full screen, as
              // in the workbench. Esc returns to the book, which posRef rebuilds
              // around the same goal, project and action.
              onWriteFullscreen={(id) => {
                // The book's editor is a different instance from zen's, so the
                // caret has to be moved deliberately — otherwise keystrokes
                // fall through to the app and become shortcuts.
                setSelectedActionId(id);
                zenFocusNextRef.current = true;
                setZenMode(true);
              }}
              updateAction={updateAction}
              deleteAction={deleteAction}
              addActionToProject={(projectId, text) => {
                // hand the new id back so the book can open it straight away
                const id = dataRef.current.nextActionId;
                addAction(text, 'project', projectId);
                return id;
              }}
              addProjectInline={addProjectInline}
              setActionStatus={setActionStatus}
              toggleActionPho={toggleActionPho}
              toggleActionComplete={toggleActionComplete}
              addChecklistItem={addChecklistItem}
              getProjectColorById={getProjectColorById}
              timeAgo={timeAgo}
            />
          )}

          {calendarView && !milestoneView && !zenMode && !ledgerView && (
            <CalendarView
              actions={actions}
              projects={projects}
              getProjectColorById={getProjectColorById}
              onOpenAction={(id) => {
                zenNavDirRef.current = 0;
                setSelectedActionId(id);
                setZenMode(true); // Esc lands back on the calendar
              }}
            />
          )}

          {!milestoneView && <>
          {/* Main Column - Actions.
              No pane open (or pane overlaying on narrow windows): list owns the space.
              Pane open beside it: fixed width, clamped so the pane keeps ≥480px.
              Zen mode: hidden. */}
          {!zenMode && !calendarView && !ledgerView && !wallMode && <div
            style={{
              ...(roomList && selectedActionId && !paneOverlay
                // The room list: a fixed rail of tasks; the note owns the rest.
                ? { width: Math.max(280, Math.min(listWidth, 440)), flexShrink: 0 }
                : ((selectedActionId && !paneOverlay) && !(minimalSolo && kanbanMode)) || (kanbanMode && !minimalSolo)
                // In the solo room the board owns the whole width until a card
                // opens its panel — nothing else is on the floor.
                ? { width: kanbanMode ? kanbanWidth : Math.max(240, Math.min(listWidth, windowWidth - (sidebarCollapsed ? 48 : 280) - 480)), flexShrink: 0 }
                : { flex: 1, minWidth: 0 }),
              // the room's rail (or board) scrolls by itself
              ...(minimalSolo ? { overflowY: 'auto' } : {}),
            }}
            className="flex flex-col overflow-hidden"
          >
            {/* ══ LIST & BOARD — the fast lanes ═══════════════════════════
                Goals view is where a goal or a project shows its face: card,
                plan, rollup, the whole picture. These two surfaces are not
                that. The left nav names the goal and the project, so both go
                straight to the action — one thin crumb strip, then rows or
                columns. The day-book skeuomorphism that briefly lived here
                (desk, leaf, masthead-as-project-card) was retired Aug 2026:
                it read as a different theme and duplicated the nav. ═══════ */}
            {kanbanMode ? (
            <div className="themed-background" style={{
              backgroundImage: 'linear-gradient(var(--color-gridLine) 1px, transparent 1px), linear-gradient(90deg, var(--color-gridLine) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}>

              {/* Board header — hidden in the solo room, where the masthead
                  already says everything this strip would */}
              {!minimalSolo && (
              <div className="px-4 py-2 border-b themed-border-light flex items-center justify-between">
                <span className="text-xs font-medium themed-text-muted">{wpCrumb}</span>
                <div className="flex items-center gap-2">
                  {/* ✦ Focus pill — stars choose, F narrows. */}
                  {(wpStarred > 0 || focusMode) && (
                    <button
                      onClick={() => setFocusMode(m => !m)}
                      title={focusMode ? 'Exit focus (F or Esc)' : 'Show starred only (F)'}
                      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors"
                      style={focusMode
                        ? { background: 'var(--color-pho)', color: 'var(--color-background)', borderColor: 'var(--color-pho)' }
                        : { color: 'var(--color-pho)', borderColor: 'var(--color-phoBorder)', background: 'var(--color-phoLight)' }}
                    >
                      ✦ Focus{wpStarred > 0 ? ` ${wpStarred}` : ''}
                    </button>
                  )}
                </div>
              </div>
              )}

              {/* No charter peek here. Goals view is where a project shows its
                  face; the Board and the List are fast lanes to the action. */}

              {/* Inline Action Add */}
              {showInlineAdd && (
                <div className="px-4 py-2.5 border-b themed-border-light">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[10px] uppercase tracking-widest themed-text-muted">Adding to:</span>
                    <select
                      value={selectedProjectId || (openFeatureId && openFeatureId !== 'unassigned' ? String(openFeatureId) : '')}
                      onChange={e => setSelectedProjectId(e.target.value)}
                      className="text-[11px] font-medium themed-primary border-0 bg-transparent focus:outline-none cursor-pointer"
                      style={{padding:0}}
                    >
                      <option value="">No project</option>
                      {projects.filter(p => p.active).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={newActionText}
                    onChange={e => setNewActionText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && newActionText.trim()) {
                        addActionFromModal();
                        setShowInlineAdd(false);
                      } else if (e.key === 'Escape') {
                        setShowInlineAdd(false);
                        setNewActionText('');
                      }
                    }}
                    placeholder="New action…"
                    className="flex-1 px-3 py-1.5 text-sm border themed-border rounded-lg themed-background themed-text focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button
                    onClick={() => { addActionFromModal(); setShowInlineAdd(false); }}
                    disabled={!newActionText.trim()}
                    className="px-3 py-1.5 text-sm themed-button-primary rounded-lg disabled:opacity-40"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => { setShowInlineAdd(false); setNewActionText(''); }}
                    className="text-sm themed-text-muted hover:themed-text"
                  >
                    ✕
                  </button>
                  </div>
                </div>
              )}

              <div className="p-6">
                <KanbanBoard
                  actionsToShow={actionsToShow}
                  completedActionsToShow={completedActionsToShow}
                  groupByProject={groupByProject && !soloProject}
                  soloProject={soloProject}
                  dragOverCol={dragOverCol}
                  setDragOverCol={setDragOverCol}
                  setActionStatus={setActionStatus}
                  selectedActionId={selectedActionId}
                  setSelectedActionId={setSelectedActionId}
                  projects={projects}
                  getProjectColorById={getProjectColorById}
                  toggleActionPho={toggleActionPho}
                  onReorderCard={sortOption === 'manual' ? handleCardReorder : undefined}
                />

                {/* Completed Today Section */}
                {!focusMode && completedActions.length > 0 && (
                  <div className="border-t themed-border-light mt-6">
                    <div
                      className="px-4 py-2.5 cursor-pointer hover:themed-surface-hover transition-all duration-150 flex items-center gap-2"
                      onClick={() => setShowCompleted(!showCompleted)}
                    >
                      <CheckCircle2 size={13} className="themed-success flex-shrink-0"/>
                      <span className="text-[12px] font-medium themed-text-muted flex-1">
                        Done today ({completedActions.length})
                      </span>
                      <ChevronRight className={`transform transition-transform duration-200 themed-text-muted ${showCompleted ? 'rotate-90' : ''}`} size={13} />
                    </div>

                    {showCompleted && (
                      <div className="p-4 space-y-3 themed-surface">
                        {completedActions.map((action) => {
                          const linkDisplay = getActionLinkDisplay(action);
                          const completedTime = action.completedDate ?
                            new Date(action.completedDate).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            }) : '';
                          const outcome = action.resolutionType === 'wontdo' ? { glyph: '○', label: 'Let go' }
                            : action.resolutionType === 'moved' ? { glyph: '→', label: 'Moved on' }
                            : { glyph: '●', label: 'Done' };
                          return (
                            <div
                              key={action.id}
                              className="border themed-border rounded-lg p-4 transition-all duration-200"
                              style={{ background: 'var(--color-landed)' }}
                            >
                              <div className="flex items-start space-x-3">
                                <button
                                  onClick={() => toggleActionComplete(action.id)}
                                  className="mt-0.5 transition-all duration-200 transform hover:scale-110"
                                  title="Reopen"
                                >
                                  <CheckCircle2 className="themed-success" size={18} />
                                </button>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[13px] themed-text-secondary">
                                    {action.text}
                                  </div>
                                  {action.resolutionComment && (
                                    <div className="text-sm themed-text italic mt-1">“{action.resolutionComment}”</div>
                                  )}
                                  <div className="flex items-center space-x-3 text-xs themed-text-muted mt-1.5">
                                    <span>{outcome.glyph} {outcome.label}{completedTime ? ` · ${completedTime}` : ''}</span>
                                    {linkDisplay.text && (
                                      <div className="flex items-center gap-2">
                                        <div
                                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                          style={{ backgroundColor: linkDisplay.dotColor }}
                                          title={`${linkDisplay.type === 'project' ? 'Project' : 'Goal'}: ${linkDisplay.fullText}`}
                                        ></div>
                                        <span className={`${linkDisplay.color} font-medium`} title={linkDisplay.fullText}>
                                          {linkDisplay.text}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Archive toggle (kept inline if archive is shown) */}
                {archivedActions.length > 0 && showArchive && (
                  <div className="mt-4">
                    <button onClick={() => setShowArchive(false)} className="text-xs themed-text-muted hover:themed-text">
                      Hide Archive
                    </button>
                  </div>
                )}

                {/* Archived Actions Panel */}
                {showArchive && (
                  <div className="mt-8 p-6 themed-surface rounded-lg border themed-border">
                    <h3 className="text-lg font-medium mb-4">📦 Archived Actions</h3>
                    {archivedActions.length === 0 ? (
                      <p className="text-sm themed-text-muted">No archived items.</p>
                    ) : (
                      archivedActions.map(action => (
                        <div key={action.id} className="mb-3 p-3 themed-background rounded-lg border themed-border">
                          <div className="flex justify-between items-center">
                            <span className={action.completedDate ? 'themed-text-secondary' : 'themed-text'}>
                              {action.text}
                            </span>
                            <span className="text-xs themed-text-muted">
                              {action.completedDate
                                ? `Done ${action.completedDate}`
                                : `Deferred to ${action.deferToDate}`}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            ) : (
            // text-left is load-bearing: an ancestor centres, and rows must
            // rag right (the retired desk carried this reset on .wp-desk)
            <div className="themed-background text-left" style={{
              backgroundImage: 'linear-gradient(var(--color-gridLine) 1px, transparent 1px), linear-gradient(90deg, var(--color-gridLine) 1px, transparent 1px)',
              backgroundSize: '28px 28px',
            }}>

              {/* List header — the Board's chrome, exactly. Hidden in the
                  solo room: the masthead says it all, N still adds, F still
                  narrows, and the sort keeps its last setting. */}
              {!minimalSolo && (
              <div className="px-4 py-2 border-b themed-border-light flex items-center gap-3">
                <span className="text-xs font-medium themed-text-muted truncate flex-1 min-w-0">{wpCrumb}</span>
                <div className="flex items-center gap-1.5">
                  <label htmlFor="wp-sort" className="text-[10px] uppercase tracking-widest themed-text-muted">Arranged by</label>
                  <select
                    id="wp-sort"
                    value={sortOption}
                    onChange={(e) => setSortOption(e.target.value)}
                    className="text-[11px] font-medium themed-text border-0 bg-transparent focus:outline-none cursor-pointer"
                    title="Sort — Manual keeps your drag-arranged order"
                  >
                    <option value="manual">Manual</option>
                    <option value="dodate">By do date</option>
                    <option value="starred">Starred first</option>
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="project">By project</option>
                  </select>
                </div>
                {/* ✦ Focus pill — stars choose, F narrows. Same markup as the Board's. */}
                {(wpStarred > 0 || focusMode) && (
                  <button
                    onClick={() => setFocusMode(m => !m)}
                    title={focusMode ? 'Exit focus (F or Esc)' : 'Show starred only (F)'}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors"
                    style={focusMode
                      ? { background: 'var(--color-pho)', color: 'var(--color-background)', borderColor: 'var(--color-pho)' }
                      : { color: 'var(--color-pho)', borderColor: 'var(--color-phoBorder)', background: 'var(--color-phoLight)' }}
                  >
                    ✦ Focus{wpStarred > 0 ? ` ${wpStarred}` : ''}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setShowInlineAdd(v => !v); setNewActionText(''); }}
                  className="p-1 rounded themed-text-muted hover:themed-text transition-colors"
                  title="New action (N)"
                >
                  <Plus size={14} />
                </button>
              </div>
              )}

              {/* Inline Action Add — the Board's strip */}
              {showInlineAdd && (
                <div className="px-4 py-2.5 border-b themed-border-light">
                  <div className="flex items-center gap-1.5 mb-2">
                    <span className="text-[10px] uppercase tracking-widest themed-text-muted">Adding to:</span>
                    <select
                      value={selectedProjectId || (openFeatureId && openFeatureId !== 'unassigned' ? String(openFeatureId) : '')}
                      onChange={e => setSelectedProjectId(e.target.value)}
                      className="text-[11px] font-medium themed-primary border-0 bg-transparent focus:outline-none cursor-pointer"
                      style={{padding:0}}
                    >
                      <option value="">No project</option>
                      {projects.filter(p => p.active).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      type="text"
                      value={newActionText}
                      onChange={e => setNewActionText(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && newActionText.trim()) {
                          addActionFromModal();
                          setShowInlineAdd(false);
                        } else if (e.key === 'Escape') {
                          setShowInlineAdd(false);
                          setNewActionText('');
                        }
                      }}
                      placeholder="New action…"
                      className="flex-1 px-3 py-1.5 text-sm border themed-border rounded-lg themed-background themed-text focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <button
                      onClick={() => { addActionFromModal(); setShowInlineAdd(false); }}
                      disabled={!newActionText.trim()}
                      className="px-3 py-1.5 text-sm themed-button-primary rounded-lg disabled:opacity-40"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => { setShowInlineAdd(false); setNewActionText(''); }}
                      className="text-sm themed-text-muted hover:themed-text"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}

                    {/* ══ THE SHEET — ruled entries behind a margin rule ══ */}
                    <div className="wp-sheet" data-manual={sortOption === 'manual' ? '1' : '0'}>
                      {actionsToShow.length === 0 ? (
                        <div className="wp-blank">
                          <div className="g">{focusMode ? 'Nothing starred here.' : 'Nothing on this sheet yet.'}</div>
                          <div className="s">{focusMode ? 'Star ✦ a line and it will be waiting here.' : 'The ＋ opens a fresh line.'}</div>
                        </div>
                      ) : listRenderItems.map((item) => {
                        if (item.type === 'lane') {
                          // The board's lane labels, rotated into the sheet
                          const laneColor = item.lane === 'doing' ? 'var(--color-warning)' : 'var(--color-primary)';
                          return (
                            <div key={`lane-${item.lane}`} className="flex items-baseline gap-1.5 px-4 pt-4 pb-2">
                              <span className="text-[10.5px] font-semibold select-none"
                                style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace", color: laneColor }}>
                                {item.lane === 'doing' ? 'active' : 'open'}
                              </span>
                              <span className="text-[10.5px] tabular-nums select-none"
                                style={{ fontFamily: "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace", color: 'var(--color-faint)' }}>
                                {item.count}
                              </span>
                            </div>
                          );
                        }
                        if (item.type === 'header') {
                          const { project, count } = item;
                          return (
                            <div key={`h-${project ? project.id : 'none'}`} className="wp-sec">
                              <span
                                className={`ink${project ? '' : ' none'}`}
                                style={project ? { background: getProjectColorById(project.id) } : undefined}
                              />
                              <span className="hd">
                                <span className="nm">{project ? project.name : 'No project'}</span>
                                <span className="wp-lead" />
                                <span className="n">{count} {count === 1 ? 'action' : 'actions'}</span>
                              </span>
                            </div>
                          );
                        }
                        const { action } = item;
                        const isCompleting = completingAction === action.id;
                        const isFlashing = phoToggleFlash.has(action.id);
                        const overdue = action.dueDate && isPast(action.dueDate);
                        const hasRight = action.status === 'doing' || !!action.doDate || !!action.dueDate;
                        return (
                          <div
                            key={action.id}
                            draggable={sortOption === 'manual' && editingAction !== action.id}
                            onDragStart={(e) => { e.dataTransfer.setData('actionId', String(action.id)); e.dataTransfer.effectAllowed = 'move'; }}
                            onDragOver={(e) => {
                              if (sortOption !== 'manual') return;
                              e.preventDefault();
                              const r = e.currentTarget.getBoundingClientRect();
                              const before = e.clientY < r.top + r.height / 2;
                              setDragOverRow(cur => (cur?.id === action.id && cur?.before === before) ? cur : { id: action.id, before });
                            }}
                            onDragLeave={() => setDragOverRow(cur => (cur?.id === action.id ? null : cur))}
                            onDrop={(e) => {
                              if (sortOption !== 'manual') return;
                              e.preventDefault();
                              const draggedId = parseInt(e.dataTransfer.getData('actionId'));
                              const r = e.currentTarget.getBoundingClientRect();
                              if (draggedId) reorderAction(draggedId, action.id, e.clientY < r.top + r.height / 2);
                            }}
                            style={dragOverRow?.id === action.id
                              ? { boxShadow: dragOverRow.before ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)' }
                              : undefined}
                            className={`wp-ln${action.status === 'doing' ? ' doing' : ''}${action.pho ? ' star' : ''}${action.id === selectedActionId ? ' sel' : ''}${hasRight ? ' has-right' : ''}${isCompleting ? ' action-completing' : ''}${isFlashing ? ' flash' : ''}`}
                          >
                            {/* the compositor's grip — printed dots, only while the order is yours */}
                            <span className="wp-grip" aria-hidden="true">⠿</span>
                            <button
                              type="button"
                              onClick={() => toggleActionComplete(action.id)}
                              className={`wp-box${action.completed ? ' on' : ''}`}
                              aria-label={`Mark done: ${action.text}`}
                              title="Mark done"
                            >
                              {action.completed && <Check size={9} strokeWidth={3} />}
                            </button>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleActionPho(action.id); }}
                              className="wp-st"
                              aria-pressed={!!action.pho}
                              title={action.pho ? 'Take your star off' : 'Star it — your own mark (floats to today)'}
                            >✦</button>
                            <span
                              className="wp-txt"
                              onClick={(e) => {
                                const tag = e.target.tagName.toLowerCase();
                                if (!['button', 'svg', 'path', 'input'].includes(tag) && !e.target.closest('button')) setSelectedActionId(action.id);
                              }}
                            >
                              {editingAction === action.id ? (
                                <input
                                  type="text"
                                  defaultValue={action.text}
                                  className="wp-edit"
                                  autoFocus
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { updateAction(action.id, { text: e.target.value }); setEditingAction(null); }
                                    else if (e.key === 'Escape') setEditingAction(null);
                                  }}
                                  onBlur={(e) => { updateAction(action.id, { text: e.target.value }); setEditingAction(null); }}
                                />
                              ) : (
                                <span className="wp-tt">{action.text}</span>
                              )}
                              {/* the leader only ever leads TO something */}
                              <span className="wp-lead" />
                            </span>
                            {hasRight ? (
                              <span className="wp-right">
                                {action.status === 'doing' && <span className="wp-stamp">in motion</span>}
                                {action.doDate && (
                                  <span className="wp-when do">Do {fmtCivil(action.doDate, { month: 'short', day: 'numeric' })}</span>
                                )}
                                {action.dueDate && (
                                  <span className={`wp-when ${overdue ? 'od' : 'due'}`}>
                                    {overdue ? 'Overdue ' : 'Due '}{fmtCivil(action.dueDate, { month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </span>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>

                    {/* ── the tail of the sheet: done ─────────────────────
                        Scoped to the SAME filter as the rows above it. It used
                        to print the whole workspace's done-today under a sheet
                        filtered to one project — "1 done" in the heading, 11
                        in the tail — a list of other projects' finishes. */}
                    {!focusMode && completedActionsToShow.length > 0 && (
                      <div className="wp-tail">
                        <button
                          type="button"
                          className="wp-tailhd"
                          aria-expanded={showCompleted}
                          onClick={() => setShowCompleted(!showCompleted)}
                        >
                          <span>Done today</span>
                          <span className="wp-lead" />
                          <span className="n">{completedActionsToShow.length} {completedActionsToShow.length === 1 ? 'action' : 'actions'}</span>
                          <span className="chev" aria-hidden="true">▸</span>
                        </button>
                        {showCompleted && (
                          <div>
                            {completedActionsToShow.map((action) => {
                              const linkDisplay = getActionLinkDisplay(action);
                              const completedTime = action.completedDate
                                ? new Date(action.completedDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                                : '';
                              // The Settle: a struck entry, not a redaction — the
                              // title demoted to a citation, the closing line
                              // carrying the meaning in the user's own words.
                              const outcome = action.resolutionType === 'wontdo' ? 'let go'
                                : action.resolutionType === 'moved' ? 'moved on'
                                : 'done';
                              return (
                                <div key={action.id} className="wp-dn">
                                  <button
                                    type="button"
                                    className="rb"
                                    onClick={() => toggleActionComplete(action.id)}
                                    title="Put it back on the sheet"
                                    aria-label={`Reopen: ${action.text}`}
                                  >
                                    <Check size={9} strokeWidth={3} />
                                  </button>
                                  <div className="body">
                                    <div className="t">{action.text}</div>
                                    {action.resolutionComment && <div className="said">“{action.resolutionComment}”</div>}
                                  </div>
                                  <div className="end">
                                    <span className="how">{outcome}</span>
                                    <span className="at">
                                      {completedTime}
                                      {linkDisplay.text && (
                                        <span className="where" title={linkDisplay.fullText}>
                                          <i style={{ background: linkDisplay.dotColor }} />
                                          {linkDisplay.text}
                                        </span>
                                      )}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

              {/* Archive toggle (kept inline if archive is shown) */}
              {archivedActions.length > 0 && showArchive && (
                <div className="mt-4 px-4">
                  <button onClick={() => setShowArchive(false)} className="text-xs themed-text-muted hover:themed-text">
                    Hide Archive
                  </button>
                </div>
              )}

              {/* Archived Actions Panel */}
              {showArchive && (
                <div className="mt-8 p-6 themed-surface rounded-lg border themed-border">
                  <h3 className="text-lg font-medium mb-4">📦 Archived Actions</h3>
                  {archivedActions.length === 0 ? (
                    <p className="text-sm themed-text-muted">No archived items.</p>
                  ) : (
                    archivedActions.map(action => (
                      <div key={action.id} className="mb-3 p-3 themed-background rounded-lg border themed-border">
                        <div className="flex justify-between items-center">
                          <span className={action.completedDate ? 'themed-text-secondary' : 'themed-text'}>
                            {action.text}
                          </span>
                          <span className="text-xs themed-text-muted">
                            {action.completedDate
                              ? `Done ${action.completedDate}`
                              : `Deferred to ${action.deferToDate}`}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            )}
          </div>}

          {/* Resize handle — only when list and pane sit side by side */}
          {!zenMode && !calendarView && !ledgerView && !wallMode && !paneOverlay && ((selectedActionId && !(minimalSolo && kanbanMode)) || (kanbanMode && !minimalSolo)) && (
            <div
              onMouseDown={startResize}
              className="w-1.5 flex-shrink-0 cursor-col-resize hover:bg-blue-400 transition-colors duration-150"
              style={{ background: 'transparent', borderLeft: '1px solid var(--color-border)' }}
            />
          )}

          {/* In calendar view the pane appears only via zen (chip → note) */}
          {((!calendarView && !ledgerView) || zenMode) && <DetailPane
            calm={minimalSolo || wallMode}
            fixedOpen={minimalSolo && !kanbanMode}
            dock={wallMode ? paneDock : 'right'}
            onDockChange={wallMode ? setPaneDock : undefined}
            completeWithReceipt={completeWithReceipt}
            narrow={minimalSolo && kanbanMode}
            selectedAction={selectedAction}
            selectedActionId={selectedActionId}
            setSelectedActionId={setSelectedActionId}
            zenMode={zenMode}
            setZenMode={setZenMode}
            filmstrip={filmstrip}
            setFilmstrip={setFilmstrip}
            displayedActions={displayedActions}
            navigateZen={navigateZen}
            zenNavDirRef={zenNavDirRef}
            zenPaneRef={zenPaneRef}
            paneOverlay={paneOverlay}
            actionEditorRef={actionEditorRef}
            updateAction={updateAction}
            deleteAction={deleteAction}
            setActionStatus={setActionStatus}
            addChecklistItem={addChecklistItem}
            toggleActionPho={toggleActionPho}
            toggleActionComplete={toggleActionComplete}
            projects={projects}
            goals={goals}
            getProjectColorById={getProjectColorById}
            formatDateTime={formatDateTime}
            dismissedColdOpenFor={dismissedColdOpenFor}
            setDismissedColdOpenFor={setDismissedColdOpenFor}
            timeAgo={timeAgo}
            onOpenGoal={(id) => openLedgerAt('goal', id)}
            onOpenProject={(id) => openLedgerAt('project', id)}
            zenClickEnterRef={zenClickEnterRef}
          />}
          </>}

        </div>
      </div>

      {/* Project Import Modal */}
      {showProjectImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50" onClick={() => setShowProjectImportModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-6 flex flex-col gap-4" onClick={e => e.stopPropagation()} style={{maxHeight: '85vh'}}>
            <div>
              <h3 className="font-semibold text-gray-900 text-base">Import projects</h3>
              <p className="text-xs text-gray-400 mt-0.5">Paste a JSON array. Projects and goals are matched by name or created if new.</p>
            </div>

            <textarea
              value={projectImportJson}
              onChange={e => { setProjectImportJson(e.target.value); setProjectImportError(''); setProjectImportResult(null); }}
              placeholder={'[\n  {\n    "goal": "Q3 OKRs",\n    "project": "Redesign Onboarding",\n    "actions": [\n      {\n        "title": "Write PRD",\n        "notes": "## Context\\nUser drop-off at step 3.",\n        "status": "todo",\n        "starred": true,\n        "checklist": [\n          { "text": "Interview 5 users", "done": false },\n          { "text": "Draft spec", "done": false }\n        ]\n      }\n    ]\n  }\n]'}
              rows={16}
              className="w-full px-3 py-2.5 text-xs font-mono border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-gray-900"
              autoFocus
              spellCheck={false}
            />

            {projectImportError && (
              <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{projectImportError}</p>
            )}
            {projectImportResult && (
              <p className="text-sm text-green-700 bg-green-50 px-3 py-2 rounded-lg">
                ✓ Imported {projectImportResult.importedProjects} project{projectImportResult.importedProjects !== 1 ? 's' : ''} and {projectImportResult.importedActions} action{projectImportResult.importedActions !== 1 ? 's' : ''}
              </p>
            )}

            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowProjectImportModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                {projectImportResult ? 'Close' : 'Cancel'}
              </button>
              {!projectImportResult && (
                <button
                  onClick={runProjectImport}
                  disabled={!projectImportJson.trim()}
                  className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-40 transition-colors"
                >
                  Import
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Modal */}
      {completionModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => setCompletionModal(null)}
        >
          {/* The done ritual — the product's most emotional modal, finally in
              the user's own theme instead of hardcoded white-and-gray (it was
              flashing another product's paint in Midnight and Poster). Title
              asks the product's own question; the confirm button answers in
              the word the user chose. UX-16 + the panel's ruling. */}
          <div
            className="rounded-xl shadow-2xl w-full max-w-md mx-4 p-6 border themed-border themed-background"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="font-semibold themed-text text-base mb-1">How did it end?</h3>
            <p className="text-sm themed-text-muted mb-5 leading-snug line-clamp-2">{completionModal.actionText}</p>

            {/* Resolution type */}
            <div className="flex gap-2 mb-5">
              {[
                { id: 'done',    label: '✓ Done',      desc: 'It happened' },
                { id: 'wontdo',  label: '✗ Won\'t do', desc: 'Not doing this' },
                { id: 'moved',   label: '→ Moved',     desc: 'It went somewhere else' },
              ].map(r => (
                <button
                  key={r.id}
                  onClick={() => setCompletionResolution(r.id)}
                  className="flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-all"
                  style={completionResolution === r.id
                    ? { background: 'var(--color-text)', color: 'var(--color-background)', borderColor: 'var(--color-text)' }
                    : { borderColor: 'var(--color-border)', color: 'var(--color-textSecondary)' }}
                  title={r.desc}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <textarea
              value={completionComment}
              onChange={e => setCompletionComment(e.target.value)}
              placeholder="How did this end? What did you decide, finish, or learn?"
              rows={3}
              className="w-full px-3 py-2.5 text-sm border themed-border rounded-lg resize-none focus:outline-none themed-background themed-text mb-4"
              style={{ background: 'var(--color-surface)' }}
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) confirmComplete(); if (e.key === 'Escape') setCompletionModal(null); }}
            />
            <p className="text-[11px] themed-text-muted -mt-2 mb-4">This line travels with the action — and ⌘K finds it later.</p>

            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setCompletionModal(null)}
                className="px-4 py-2 text-sm themed-text-muted hover:themed-text transition-colors"
              >
                Not yet
              </button>
              <button
                onClick={confirmComplete}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
                style={{ background: 'var(--color-text)', color: 'var(--color-background)' }}
              >
                {completionResolution === 'wontdo' ? 'Won\'t do' : completionResolution === 'moved' ? 'Moved on' : 'Mark as done'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unified Create Modal — disabled, replaced by inline adds in sidebar */}
      {false && showCreateModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={resetCreateModal}
        >
          <div 
            className="themed-background rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Tabs */}
            <div className="p-6 border-b themed-border-light">
              <h3 className="text-xl font-semibold themed-text mb-4">
                {createModalTab === 'import' ? 'Import Actions' : 'Create New'}
              </h3>
              <div className="flex space-x-1 themed-surface rounded-lg p-1">
                <button
                  onClick={() => setCreateModalTab('action')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1 ${
                    createModalTab === 'action' 
                      ? 'themed-background themed-primary shadow-sm' 
                      : 'themed-text-secondary hover:themed-text'
                  }`}
                >
                  <Target size={14} />
                  <span>Action</span>
                </button>
                <button
                  onClick={() => setCreateModalTab('project')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1 ${
                    createModalTab === 'project'
                      ? 'themed-background themed-primary shadow-sm'
                      : 'themed-text-secondary hover:themed-text'
                  }`}
                >
                  <Briefcase size={14} />
                  <span>Project</span>
                </button>
                <button
                  onClick={() => setCreateModalTab('goal')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1 ${
                    createModalTab === 'goal'
                      ? 'themed-background themed-primary shadow-sm'
                      : 'themed-text-secondary hover:themed-text'
                  }`}
                >
                  <Building size={14} />
                  <span>Goal</span>
                </button>
                <button
                  onClick={() => setCreateModalTab('import')}
                  className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 flex items-center justify-center space-x-1 ${
                    createModalTab === 'import' 
                      ? 'themed-background themed-primary shadow-sm' 
                      : 'themed-text-secondary hover:themed-text'
                  }`}
                >
                  <Upload size={14} />
                  <span>Import</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {createModalTab === 'action' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Action</label>
                    <input
                      type="text"
                      value={newActionText}
                      onChange={(e) => setNewActionText(e.target.value)}
                      placeholder="What will you accomplish today? ✨"
                      className="w-full p-4 text-lg border-2 themed-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newActionText.trim()) {
                          e.preventDefault();
                          addActionFromModal();
                        }
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Assign to Project</label>
                    <select 
                      value={selectedProjectId}
                      onChange={(e) => setSelectedProjectId(e.target.value)}
                      className="w-full p-3 border themed-border rounded-lg themed-background themed-text"
                    >
                      <option value="">🔗 Unassigned</option>
                      {projects.filter(p => p.active).map((project) => {
                        const displayText = `📋 ${project.name}`;
                        
                        return (
                          <option 
                            key={project.id} 
                            value={project.id} 
                            title={project.name}
                            className="text-sm"
                          >
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
              )}

              {createModalTab === 'project' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Project Name</label>
                    <input
                      type="text"
                      value={newProjectName}
                      onChange={(e) => setNewProjectName(e.target.value)}
                      placeholder="Enter project name..."
                      className="w-full p-4 text-lg border-2 themed-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newProjectName.trim()) {
                          e.preventDefault();
                          addProject();
                        }
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Link to Goal (Optional)</label>
                    <select 
                      value={newProjectGoal}
                      onChange={(e) => setNewProjectGoal(e.target.value)}
                      className="w-full p-3 border themed-border rounded-lg themed-background themed-text"
                    >
                      <option value="">🔗 No Goal (Standalone Project)</option>
                      {goals.filter(i => i.active).map((goal) => (
                        <option key={goal.id} value={goal.id}>
                          🎯 {goal.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {createModalTab === 'goal' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Goal Name</label>
                    <input
                      type="text"
                      value={newGoalName}
                      onChange={(e) => setNewGoalName(e.target.value)}
                      placeholder="Enter goal name..."
                      className="w-full p-4 text-lg border-2 themed-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && newGoalName.trim()) {
                          e.preventDefault();
                          addGoal();
                        }
                      }}
                    />
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900 bg-opacity-30 p-4 rounded-lg border border-blue-200 dark:border-blue-700">
                    <div className="text-sm themed-text">
                      <strong>💡 What's an Goal?</strong>
                      <p className="mt-1 themed-text opacity-80">
                        An goal is a high-level strategic action that contains multiple related projects. 
                        For example: "Increase User Engagement" could contain projects like "Redesign Dashboard" and "Add Gamification Features".
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {createModalTab === 'import' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Paste Actions (One Per Line)</label>
                    <textarea
                      value={bulkImportText}
                      onChange={(e) => setBulkImportText(e.target.value)}
                      placeholder="Enter one action per line:&#10;&#10;Complete project proposal&#10;Review team feedback&#10;Schedule follow-up meeting"
                      className="w-full p-4 text-sm border-2 themed-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text resize-none"
                      rows={8}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey && bulkImportText.trim()) {
                          e.preventDefault();
                          importBulkActions();
                        }
                      }}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium themed-text mb-2">Assign to Project (Optional)</label>
                    <select 
                      value={importTargetProject}
                      onChange={(e) => setImportTargetProject(e.target.value)}
                      className="w-full p-3 border themed-border rounded-lg themed-background themed-text"
                    >
                      <option value="">🔗 Leave Unassigned</option>
                      {projects.filter(p => p.active).map((project) => {
                        const displayText = `📋 ${project.name}`;
                        
                        return (
                          <option 
                            key={project.id} 
                            value={project.id} 
                            title={project.name}
                            className="text-sm"
                          >
                            {displayText}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <div className="text-sm text-blue-800">
                      <strong>📝 Import Preview:</strong>
                      <p className="mt-1">
                        {bulkImportText.trim() ? 
                          `${bulkImportText.trim().split('\n').filter(line => line.trim()).length} actions will be created` : 
                          'Enter actions above to see preview'
                        }
                      </p>
                      {importTargetProject && (
                        <p className="mt-1">
                          All actions will be assigned to: <strong>{projects.find(p => p.id === parseInt(importTargetProject))?.name}</strong>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

            </div>

            
            {/* Modal Footer */}
            <div className="p-6 border-t themed-border-light flex justify-end space-x-3">
              {createModalTab === 'action' && (
                <div className="px-6 pb-2 flex items-center">
                  <input
                    type="checkbox"
                    id="createAnother"
                    checked={createAnother}
                    onChange={() => setCreateAnother(!createAnother)}
                    className="mr-2"
                  />
                  <label htmlFor="createAnother" className="text-sm themed-text">Create another</label>
                </div>
              )}
              <button
                onClick={resetCreateModal}
                className="themed-button-secondary px-6 py-2 rounded-lg"
              >
                Cancel
              </button>
              {(createModalTab === 'action' || createModalTab === 'project' || createModalTab === 'goal' || createModalTab === 'import') && (
                <button
                  onClick={() => {
                    if (createModalTab === 'action') addActionFromModal();
                    else if (createModalTab === 'project') addProject();
                    else if (createModalTab === 'goal') addGoal();
                    else if (createModalTab === 'import') importBulkActions();
                  }}
                  disabled={
                    (createModalTab === 'action' && !newActionText.trim()) ||
                    (createModalTab === 'project' && !newProjectName.trim()) ||
                    (createModalTab === 'goal' && !newGoalName.trim()) ||
                    (createModalTab === 'import' && !bulkImportText.trim())
                  }
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    ((createModalTab === 'action' && newActionText.trim()) ||
                     (createModalTab === 'project' && newProjectName.trim()) ||
                     (createModalTab === 'goal' && newGoalName.trim()) ||
                     (createModalTab === 'import' && bulkImportText.trim()))
                      ? 'themed-button-primary' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {createModalTab === 'action' ? 'Create Action' : createModalTab === 'project' ? 'Create Project' : createModalTab === 'goal' ? 'Create Goal' : 'Import Actions'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Batch Assign Modal */}
      {showBatchAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="themed-background p-8 rounded-xl shadow-2xl max-w-3xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-semibold mb-6 themed-text">Assign {unlinkedActions.length} Unlinked Actions</h3>
            <div className="space-y-6">
              {unlinkedActions.map((action) => (
                <div key={action.id} className="themed-surface p-5 rounded-lg border themed-border">
                  <div className="space-y-3">
                    <div>
                      <div className="font-medium themed-text text-lg mb-1">{action.text}</div>
                      <div className="text-sm themed-text-muted flex items-center gap-3">
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <label className="text-sm font-medium themed-text min-w-[80px]">Assign to:</label>
                      <select 
                        id={`project-${action.id}`}
                        className="flex-1 p-2.5 border themed-border rounded-lg themed-background text-sm themed-text"
                        defaultValue=""
                      >
                        <option value="">🔗 Keep Unassigned</option>
                        {projects.filter(p => p.active).map((project) => {
                          const goal = goals.find(i => i.id === project.goalId);
                          const goalName = goal?.name || 'Unlinked';
                          
                          let displayText = `📋 ${project.name}`;
                          if (displayText.length + goalName.length > 50) {
                            displayText = displayText.substring(0, 40) + '...';
                          }
                          displayText += ` → ${goalName}`;
                          
                          return (
                            <option 
                              key={project.id} 
                              value={project.id} 
                              title={`${project.name} → ${goalName}`}
                              className="text-sm"
                            >
                              {displayText}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end space-x-3 pt-4 border-t themed-border-light">
                <button
                  onClick={() => setShowBatchAssignModal(false)}
                  className="themed-button-secondary px-6 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const assignments = [];
                    unlinkedActions.forEach(action => {
                      const projectId = document.getElementById(`project-${action.id}`).value;
                      
                      if (projectId) {
                        assignments.push({
                          actionId: action.id,
                          linkType: 'project',
                          linkId: parseInt(projectId)
                        });
                      }
                    });
                    
                    if (assignments.length > 0) {
                      batchAssignActions(assignments);
                    } else {
                      setShowBatchAssignModal(false);
                    }
                  }}
                  className="themed-button-primary px-6 py-2 rounded-lg"
                >
                  Apply Assignments
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="themed-background p-8 rounded-xl shadow-2xl max-w-sm w-full mx-4">
            <h3 className="text-lg font-medium mb-4 themed-text">Defer Action</h3>
            <p className="text-sm themed-text-secondary mb-6 p-3 themed-surface rounded-lg">
              "{deferringActionId && actions.find(g => g.id === deferringActionId)?.text}"
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium themed-text mb-2">Defer to date:</label>
                <input
                  type="date"
                  min={new Date().toISOString().split('T')[0]}
                  defaultValue={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                  className="w-full p-3 border-2 themed-border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text"
                  id="defer-date-input"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowDatePicker(false);
                    setDeferringActionId(null);
                  }}
                  className="themed-button-secondary px-4 py-2 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const dateInput = document.getElementById('defer-date-input');
                    confirmDefer(dateInput.value);
                  }}
                  className="themed-button-primary px-6 py-2 rounded-lg"
                >
                  Defer Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal */}
      {showChecklistModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="themed-background p-8 rounded-xl shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-6 themed-text">Add Action Item</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium themed-text mb-2">What action needs to be taken?</label>
                <input
                  type="text"
                  value={newChecklistText}
                  onChange={(e) => setNewChecklistText(e.target.value)}
                  placeholder="Enter a specific action..."
                  className="w-full p-4 text-lg border-2 themed-border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent themed-background themed-text"
                  autoFocus
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      confirmAddChecklistItem();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowChecklistModal(false);
                      setNewChecklistText('');
                      setChecklistTargetId(null);
                    }
                  }}
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowChecklistModal(false);
                    setNewChecklistText('');
                    setChecklistTargetId(null);
                  }}
                  className="themed-button-secondary px-6 py-2 rounded-lg transition-all duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAddChecklistItem}
                  disabled={!newChecklistText.trim()}
                  className={`px-6 py-2 rounded-lg transition-all duration-200 ${
                    newChecklistText.trim() 
                      ? 'themed-button-primary' 
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Add Action
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="themed-background p-8 rounded-xl shadow-2xl max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold mb-6 themed-text">Export Your Data</h3>
            <p className="text-sm themed-text-secondary mb-6">
              Download your actions, projects, and goals as a CSV file for backup or analysis.
            </p>
            
            <div className="space-y-4">
              <button
                onClick={() => {
                  exportData('active');
                  setShowExportModal(false);
                }}
                className="w-full p-4 border-2 themed-border rounded-xl hover:themed-surface-hover transition-all duration-200 text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-lg">📋</div>
                  <div>
                    <div className="font-medium themed-text">Active Items</div>
                    <div className="text-sm themed-text-secondary">Current actions & projects</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  exportData('completed');
                  setShowExportModal(false);
                }}
                className="w-full p-4 border-2 themed-border rounded-xl hover:themed-surface-hover transition-all duration-200 text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-lg">✅</div>
                  <div>
                    <div className="font-medium themed-text">Completed Items</div>
                    <div className="text-sm themed-text-secondary">Finished actions & archived</div>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => {
                  exportData('all');
                  setShowExportModal(false);
                }}
                className="w-full p-4 border-2 themed-border rounded-xl hover:themed-surface-hover transition-all duration-200 text-left"
              >
                <div className="flex items-center space-x-3">
                  <div className="text-lg">📊</div>
                  <div>
                    <div className="font-medium themed-text">Everything</div>
                    <div className="text-sm themed-text-secondary">Complete data export</div>
                  </div>
                </div>
              </button>
            </div>

            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-6">
              <div className="text-sm text-green-800">
                <strong>💾 Export Info:</strong>
                <p className="mt-1">
                  CSV files can be opened in Excel, Google Sheets, or any spreadsheet application.
                  Includes: Action text, project, goal, status, completion date, and notes.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="themed-button-secondary px-6 py-2 rounded-lg transition-all duration-200"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      
    </>
  );
};

// Main App Component with Theme Provider
const DailyFocusToolWithThemes = ({ token, user, onLogout }) => {
  return (
    <ThemeProvider>
      <style jsx global>{`
        /* CSS Custom Properties (will be overridden by theme) */
        :root {
          --color-primary: #3b82f6;
          --color-primaryHover: #2563eb;
          --color-secondary: #6366f1;
          --color-accent: #8b5cf6;
          --color-background: #ffffff;
          --color-surface: #f8fafc;
          --color-surfaceHover: #f1f5f9;
          --color-text: #1f2937;
          --color-textSecondary: #6b7280;
          --color-textMuted: #9ca3af;
          --color-border: #e5e7eb;
          --color-borderLight: #f3f4f6;
          --color-success: #10b981;
          --color-warning: #f59e0b;
          --color-error: #ef4444;
          --color-pho: #a855f7;
          --color-phoLight: #f3e8ff;
          --color-phoBorder: #c084fc;
          --font-main: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }

        /* Action container layout - CRITICAL for proper layout */
        .action-container {
          display: flex !important;
          gap: 1rem !important;
          text-align: left !important;
        }

        .action-checkbox-container {
          flex-shrink: 0 !important;
          width: 1.25rem !important;
          display: flex !important;
          justify-content: center !important;
          padding-top: 0.125rem !important;
        }

        .action-content {
          flex: 1 !important;
          min-width: 0 !important;
          text-align: left !important;
        }

        .action-text-wrapper {
          margin-bottom: 0 !important;
          text-align: left !important;
        }

        .action-text {
          display: block !important;
          margin: 0 !important;
          padding: 0 !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          line-height: 1.4 !important;
          cursor: pointer !important;
          transition: color 200ms !important;
          text-align: left !important;
          color: var(--color-text) !important;
          width: 100% !important;
        }

        .action-text:hover {
          color: var(--color-primary) !important;
        }

        .action-text.completed {
          color: var(--color-textMuted) !important;
        }

        /* Ensure proper alignment */
        .action-text-wrapper,
        .action-text-wrapper > div,
        .action-content > div {
          text-align: left !important;
        }

        /* Theme utility classes */
        .themed-background { background-color: var(--color-background); }
        .themed-surface { background-color: var(--color-surface); }
        .themed-surface-hover { background-color: var(--color-surfaceHover); }
        .themed-text { color: var(--color-text); }
        .themed-text-secondary { color: var(--color-textSecondary); }
        .themed-text-muted { color: var(--color-textMuted); }
        .themed-border { border-color: var(--color-border); }
        .themed-border-light { border-color: var(--color-borderLight); }
        .themed-primary { color: var(--color-primary); }
        .themed-primary-bg { background-color: var(--color-primary); }
        .themed-primary-border { border-color: var(--color-primary); }
        .themed-success { color: var(--color-success); }
        .themed-error { color: var(--color-error); }
        .themed-pho { color: var(--color-pho); }
        .themed-pho-bg { background-color: var(--color-pho); }
        .themed-pho-light { background-color: var(--color-phoLight); }
        .themed-pho-border { border-color: var(--color-phoBorder); }

        /* Button styles */
        .themed-button-primary {
          background-color: var(--color-primary);
          color: white;
          border: 1px solid var(--color-primary);
          transition: all 0.2s ease;
        }
        .themed-button-primary:hover {
          background-color: var(--color-primaryHover);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px color-mix(in srgb, var(--color-primary) 30%, transparent);
        }
        .themed-button-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .themed-button-secondary {
          background-color: var(--color-surface);
          color: var(--color-text);
          border: 1px solid var(--color-border);
          transition: all 0.2s ease;
        }
        .themed-button-secondary:hover {
          background-color: var(--color-surfaceHover);
          border-color: var(--color-primary);
        }

        /* Input styles */
        input[type="text"], 
        input[type="email"], 
        input[type="password"], 
        input[type="date"], 
        textarea, 
        select {
          background-color: var(--color-background);
          border-color: var(--color-border);
          color: var(--color-text);
          transition: all 0.2s ease;
        }

        input:focus, 
        textarea:focus, 
        select:focus {
          border-color: var(--color-primary);
          outline: none;
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-primary) 20%, transparent);
        }

        input::placeholder,
        textarea::placeholder {
          color: var(--color-textMuted);
        }

        /* Smooth theme transitions */
        * {
          transition: background-color 0.3s ease, 
                      color 0.3s ease, 
                      border-color 0.3s ease,
                      box-shadow 0.3s ease;
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          :root {
            --color-border: #000000;
            --color-text: #000000;
            --color-background: #ffffff;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
      <DailyFocusTool token={token} user={user} onLogout={onLogout} />
    </ThemeProvider>
  );
};

export { DailyFocusToolWithThemes as default, ThemeProvider, useTheme, ThemeSwitcher };